<!-- Copyright (c) 2023 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="company-main">
    <Menus :menu="menus" @menuClicked="menuClicked" />
    <div class="right-container">
      <TableBox
        title="Company Profile"
        :tableData="companyList"
        :selectedRow.sync="selectedCompany"
        :loading="companyLoading"
      />
    </div>

    <PopupEntry
      :show.sync="popupEntryShow"
      v-if="popupEntryShow"
      :title="popupEntryTitle"
      createdBy=""
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="entryRequired"
      :successMessages="entrySuccess"
      :messages="entryMessages"
      :errorMessages="entryError"
      @requiredClicked="entryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <Tab :tabs="tabsItems" style="height: 100%" :tabIndex.sync="currentTab">
        <div slot="General Details" style="display: flex; height: 100%">
          <!-- General Details -->
          <div class="gen-details-right">
            <Input
              placeholder="Company Code"
              :inputValue.sync="companyCode"
              :errorMessage.sync="companyCodeError"
              :isRequired="true"
              ref="companyCode"
              :isDisabled="viewMode || popupEntryTitle[0] == 'E'"
              validator="masterfilecode,2"
            />
            <div class="flex-row">
              <Input
                :isDisabled="viewMode || popupEntryTitle[0] == 'E'"
                placeholder="Name"
                :isRequired="true"
                ref="co_name"
                :inputValue.sync="co_name"
                :errorMessage.sync="nameError"
              />
              <Input
                :isDisabled="viewMode || popupEntryTitle[0] == 'E'"
                placeholder="Short Name"
                :isRequired="true"
                ref="co_sname"
                :inputValue.sync="co_sname"
                :errorMessage.sync="snameError"
              />
            </div>
            <div class="flex-row">
              <Input
                :isDisabled="viewMode"
                placeholder="Registration No"
                :inputValue.sync="regNo"
                :errorMessage.sync="regNoError"
                ref="co_regno"
              />
              <DateTimePicker :isDisabled="viewMode" placeholder="Establish Date" :value.sync="establishDate" />
            </div>
            <Dropdown
              :isDisabled="viewMode"
              placeholder="Company Type"
              :selectedObject.sync="selectedCoType"
              :items="CoTypeList"
              itemKey="Description"
              :errorMessage.sync="coTypeError"
              ref="coType"
              :search.sync="coTypeSearch"
              style="padding-bottom: 30px"
            />
            <AddressFormV2
              :address.sync="addressData"
              :addressErrors.sync="addressErrors"
              :viewMode="viewMode"
              :addressRequired="addressRequired"
              ref="addressForm"
              :postcodes="postcodeList"
              :states="stateList"
              :countries="countryList"
            />
            <div class="flex-row">
              <Input
                :isDisabled="viewMode"
                placeholder="Phone 1"
                :inputValue.sync="phone1"
                :errorMessage.sync="phone1Error"
                ref="co_phone1"
                validator="text,20,,alphanumeric,all"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Phone 2"
                :inputValue.sync="phone2"
                :errorMessage.sync="phone2Error"
                ref="co_phone2"
                validator="text,20,,alphanumeric,all"
              />
            </div>
            <div class="flex-row">
              <Input
                :isDisabled="viewMode"
                placeholder="Fax 1"
                :inputValue.sync="co_fax1"
                :errorMessage.sync="faxError1"
                ref="co_fax1"
                validator="text,20,,alphanumeric,all"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Fax 2"
                :inputValue.sync="co_fax2"
                :errorMessage.sync="faxError2"
                ref="co_fax2"
                validator="text,20,,alphanumeric,all"
              />
            </div>
            <div class="flex-row">
              <Input
                :isDisabled="viewMode"
                placeholder="Email 1"
                :inputValue.sync="co_email1"
                :errorMessage.sync="emailError1"
                ref="co_email1"
                validator="text,40,,alphanumeric,all"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Email 2"
                :inputValue.sync="co_email2"
                :errorMessage.sync="emailError2"
                ref="co_email2"
                validator="text,40,,alphanumeric,all"
              />
            </div>
            <RemarksBox
              title="Business Description"
              :isDisabled="viewMode"
              :inputValue.sync="co_businessDesc"
              :maxLength="200"
              :text.sync="co_businessDesc"
              :errorMessage.sync="businessDescError"
              style="height: 100px"
            />
          </div>
        </div>
        <div slot="Other Details" style="height: 100%">
          <!-- Other Details -->
          <Input
            :isDisabled="viewMode"
            placeholder="Website(URL)"
            ref="co_website"
            :inputValue.sync="co_website"
            :errorMessage.sync="websiteError"
          />
          <Input
            :isDisabled="viewMode"
            placeholder="EPF No"
            :inputValue.sync="epfNo"
            :errorMessage.sync="epfNoError"
            ref="epfNo"
            validator="text"
            style="width: 50%"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="EPF Category Default"
            :selectedObject.sync="selectedEPFCategory"
            :items="EPFCategoryData"
            itemKey="EPFCategory"
            descKey="Description"
            :errorMessage.sync="epfCategoryError"
            :search.sync="epfCategorySearch"
            ref="epfCategory"
          />
          <Input
            :isDisabled="viewMode"
            placeholder="SOCSO No"
            :inputValue.sync="socsoNo"
            :errorMessage.sync="socsoNoError"
            ref="socsoNo"
            validator="text"
            style="width: 50%"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="SOCSO Category Default"
            :selectedObject.sync="selectedSOCSOCategory"
            :items="SOCSOCategoryData"
            itemKey="SocsoCategory"
            descKey="Description"
            :errorMessage.sync="socsoCategoryError"
            :search.sync="socsoCategorySearch"
            ref="socsoCategory"
          />
          <Input
            :isDisabled="viewMode"
            placeholder="Income Tax No"
            :inputValue.sync="incomeTaxNo"
            :errorMessage.sync="incomeTaxNoError"
            ref="incomeTaxNo"
            validator="text"
            style="width: 50%"
          />

          <BoxContainer title="Company Logo">
            <Input
              textType="file"
              placeholder="Browse"
              :isDisabled="viewMode"
              ref="companyLogoFileInput"
              @fileChanged="companyLogoFilesChanged"
              style="width: 50%"
            />
            <div v-if="companyLogoUploadedImage">
              <div>
                <strong>Uploaded Image :</strong> <span>{{ companyLogoUploadedImage.name }}</span>
              </div>
              <img class="company-logo-img" :src="createBlobURL(companyLogoUploadedImage)" alt="Company Logo" />
            </div>
            <img class="company-logo-img" v-else-if="companyLogoBlobURL" :src="companyLogoBlobURL" alt="Company Logo" />
            <div v-else class="image-placeholder">
              <div class="placeholder-text-container">
                <span>{{ companyLogoPlaceholderMessage }}</span>
              </div>
            </div>
          </BoxContainer>
        </div>
        <div slot="SST / GL Acc / Aging / eInvoice" style="height: 100%">
          <!-- SST / GL Acc / Aging / eInvoice -->
          <CheckBox title="SST Enabled" :isChecked.sync="sstEnabled" :isDisabled="viewMode" />
          <div class="flex-row">
            <DateTimePicker
              :isDisabled="viewMode || !sstEnabled"
              placeholder="SST Effective Date"
              :value.sync="SSTEffectiveDate"
            />
            <Input
              :isDisabled="viewMode || !sstEnabled"
              placeholder="SST License No"
              :inputValue.sync="sstLicenseNo"
              :errorMessage.sync="sstLicenseNoError"
              ref="sstLicenseNo"
              style="width: 50%"
            />
          </div>
          <InputQuery
            :isDisabled="viewMode || !sstEnabled"
            placeholder="SST Submitter"
            ref="sstSubmitter"
            :items="SSTSubmitterData"
            itemKey="EmployeeID"
            descKey="Employee Name"
            :selectedObject.sync="selectedSSTSubmitter"
            :errorMessage.sync="sstSubmitterError"
            :search.sync="sstSubmitterSearch"
          />
          <InputQuery
            :isDisabled="viewMode || !sstEnabled"
            placeholder="SST GL Acc No"
            ref="sstGLAccNo"
            :items="SSTGLAccNoData"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedSSTGLAccNo"
            :errorMessage.sync="sstGLAccNoError"
            :search.sync="sstGLAccSearch"
          />
          <RadioButtonSet
            title="Run Validation when creating New SST Return"
            class="mb-2"
            :buttons="sstValidationButtons"
            labelKey="label"
            :selected.sync="sstValidationValue"
            :isHorizontal="true"
            :isDisabled="viewMode"
          />

          <Input
            placeholder="Default Service Type Code for Charge Code Master"
            :inputValue.sync="sstServiceTypeCode"
            :isDisabled="viewMode"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="P&L GL Acc No"
            ref="plGLAccNo"
            :items="PLGLAccNoData"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedPLGLAccNo"
            :errorMessage.sync="plGLAccNoError"
            :search.sync="plGLAccSearch"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="Accumulated P&L GL Acc No"
            ref="accumulatedPLGLAccNo"
            :items="AccumulatedPLGLAccNoData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="accumulatedPLGLAccSearch"
            :selectedObject.sync="selectedAccumulatedPLGLAccNo"
            :errorMessage.sync="accumulatedPLGLAccNoError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="Debtor GL Acc Code"
            ref="debtorGLAccCode"
            :items="DebtorGLAccCodeData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="debtorGLAccSearch"
            :selectedObject.sync="selectedDebtorGLAccCode"
            :errorMessage.sync="debtorGLAccCodeError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="Creditor GL Acc Code"
            ref="creditorGLAccCode"
            :items="CreditorGLAccCodeData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="creditorGLAccSearch"
            :selectedObject.sync="selectedCreditorGLAccCode"
            :errorMessage.sync="creditorGLAccCodeError"
          />
          <div class="flex-row">
            <Dropdown
              :isDisabled="viewMode"
              placeholder="Aging Type"
              :selectedObject.sync="selectedAgingType"
              :items="AgingTypeList"
              itemKey="Aging Description"
              :errorMessage.sync="agingTypeError"
              :search.sync="agingTypeSearch"
              ref="agingType"
              style="padding-bottom: 30px"
              @selected="agingType"
            />
            <Dropdown
              :isDisabled="viewMode || monthTitleIsDisabled"
              placeholder="Month Title"
              :selectedObject.sync="selectedMonthTitle"
              :items="MonthTitleList"
              itemKey="Description"
              :errorMessage.sync="monthTitleError"
              :search.sync="monthTitleSearch"
              ref="monthTitle"
              style="padding-bottom: 30px"
            />
            <Dropdown
              :isDisabled="viewMode"
              placeholder="Financial Month"
              :selectedObject.sync="selectedFinancialMonth"
              :items="monthList"
              itemKey="month"
              ref="financialMonth"
              style="padding-bottom: 30px"
              :search.sync="financialMonthSearch"
              :errorMessage.sync="financialMonthError"
            />
          </div>
          <div class="flex-row">
            <div class="rowTitle"> Aging Buckets </div>
            <Input
              :isDisabled="viewMode || selectedAgingType === null || agingTypeSearch === 'Month'"
              placeholder="1"
              ref="agingBucket1"
              :inputValue.sync="agingBucket1"
              :errorMessage.sync="agingBucketsError1"
              validator="positiveInteger,10"
              @inputChanged="agingBucket1InputChanged"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="2"
              ref="agingBucket2"
              :inputValue.sync="agingBucket2"
              :errorMessage.sync="agingBucketsError2"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="3"
              ref="agingBucket3"
              :inputValue.sync="agingBucket3"
              :errorMessage.sync="agingBucketsError3"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="4"
              ref="agingBucket4"
              :inputValue.sync="agingBucket4"
              :errorMessage.sync="agingBucketsError4"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="5"
              ref="agingBucket5"
              :inputValue.sync="agingBucket5"
              :errorMessage.sync="agingBucketsError5"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="6"
              ref="agingBucket6"
              :inputValue.sync="agingBucket6"
              :errorMessage.sync="agingBucketsError6"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="7"
              ref="agingBucket7"
              :inputValue.sync="agingBucket7"
              :errorMessage.sync="agingBucketsError7"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="8"
              ref="agingBucket8"
              :inputValue.sync="agingBucket8"
              :errorMessage.sync="agingBucketsError8"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="9"
              ref="agingBucket9"
              :inputValue.sync="agingBucket9"
              :errorMessage.sync="agingBucketsError9"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="10"
              ref="agingBucket10"
              :inputValue.sync="agingBucket10"
              :errorMessage.sync="agingBucketsError10"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="11"
              ref="agingBucket11"
              :inputValue.sync="agingBucket11"
              :errorMessage.sync="agingBucketsError11"
              validator="positiveInteger,10"
            />
            <Input
              :isDisabled="viewMode || selectedAgingType === null"
              placeholder="12"
              ref="agingBucket12"
              :inputValue.sync="agingBucket12"
              :errorMessage.sync="agingBucketsError12"
              validator="positiveInteger,10"
            />
          </div>
          <div class="flex-row">
            <Input
              :isDisabled="viewMode"
              placeholder="Default Credit limit"
              ref="defaultCreditLimit"
              :inputValue.sync="defaultCreditLimit"
              :errorMessage.sync="defaultCreditLimitError"
              validator="positiveInteger,7"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Default Credit Term"
              ref="defaultCreditTerm"
              :inputValue.sync="defaultCreditTerm"
              :errorMessage.sync="defaultCreditTermError"
              validator="positiveInteger,4"
            />
          </div>
          <div class="flex-row">
            <InputQuery
              :isDisabled="viewMode"
              placeholder="F.Cur Gain GL Acc"
              ref="fCurGainGLAcc"
              :items="FCurGainGLAccData"
              itemKey="Acc No"
              descKey="Description"
              :search.sync="fCurGainGLAccSearch"
              :selectedObject.sync="selectedFCurGainGLAcc"
              :errorMessage.sync="fCurGainGLAccError"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="F.Cur Loss GL Acc"
              ref="fCurLossGLAcc"
              :items="FCurLossGLAccData"
              itemKey="Acc No"
              descKey="Description"
              :search.sync="fCurLossGLAccSearch"
              :selectedObject.sync="selectedFCurLossGLAcc"
              :errorMessage.sync="fCurLossGLAccError"
            />
          </div>
          <div class="eInvoiceTitle">e-Invoice</div>
          <Input
            :isDisabled="viewMode"
            placeholder="Invoice TIN"
            ref="invoiceTIN"
            :inputValue.sync="invoiceTIN"
            :errorMessage.sync="invoiceTINError"
          />
          <Input
            :isDisabled="viewMode"
            placeholder="Tourism Tax No"
            ref="tourismTaxNo"
            :inputValue.sync="tourismTaxNo"
            :errorMessage.sync="tourismTaxNoError"
          />
          <Input :isDisabled="true" placeholder="Version" :inputValue.sync="version" />
        </div>
        <div slot="Report Header & Footer" style="height: 100%">
          <!-- Report Header & Footer -->
          <BoxContainer title="Report Header Text">
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 1"
              ref="reportHeader1"
              :inputValue.sync="reportHeader1"
              :errorMessage.sync="reportHeaderError1"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 2"
              ref="reportHeader2"
              :inputValue.sync="reportHeader2"
              :errorMessage.sync="reportHeaderError2"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 3"
              ref="reportHeader3"
              :inputValue.sync="reportHeader3"
              :errorMessage.sync="reportHeaderError3"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 4"
              ref="reportHeader4"
              :inputValue.sync="reportHeader4"
              :errorMessage.sync="reportHeaderError4"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 5"
              ref="reportHeader5"
              :inputValue.sync="reportHeader5"
              :errorMessage.sync="reportHeaderError5"
            />
          </BoxContainer>
          <BoxContainer title="Report Footer Text">
            <Input
              :isDisabled="viewMode"
              placeholder="Report Footer 1"
              ref="reportFooter1"
              :inputValue.sync="reportFooter1"
              :errorMessage.sync="reportFooterError1"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Footer 2"
              ref="reportFooter2"
              :inputValue.sync="reportFooter2"
              :errorMessage.sync="reportFooterError2"
            />
          </BoxContainer>
          <BoxContainer title="Report Header Image">
            <span
              >For the best experience, please ensure the image is designed on a <strong>200mm wide</strong> &times;
              <strong>25mm high</strong> canvas</span
            >
            <Input
              textType="file"
              placeholder="Browse"
              :isDisabled="viewMode"
              ref="reportHeaderFileInput"
              @fileChanged="reportHeaderImageFilesChanged"
              style="width: 50%"
            />
            <div v-if="reportHeaderUploadedImage">
              <div>
                <strong>Uploaded Image: </strong> <span>{{ reportHeaderUploadedImage.name }}</span>
              </div>
              <img
                :src="createBlobURL(reportHeaderUploadedImage)"
                alt="Report Header Image"
                style="height: 25mm; width: 200mm"
              />
            </div>
            <img
              v-else-if="reportHeaderImageBlobURL"
              :src="reportHeaderImageBlobURL"
              alt="Report Header Image"
              style="height: 25mm; width: 200mm"
            />
            <div v-else class="image-placeholder">
              <div class="placeholder-text-container">
                <span>{{ reportHeaderImagePlaceholderMessage }}</span>
              </div>
            </div>
          </BoxContainer>
        </div>
        <div slot="Fin Report Header & Footer" style="height: 100%">
          <!-- Fin Report Header & Footer -->
          <BoxContainer title="Fin Report Header Text">
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 1"
              ref="finReportHeader1"
              :inputValue.sync="finReportHeader1"
              :errorMessage.sync="finReportHeaderError1"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 2"
              ref="finReportHeader2"
              :inputValue.sync="finReportHeader2"
              :errorMessage.sync="finReportHeaderError2"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 3"
              ref="finReportHeader3"
              :inputValue.sync="finReportHeader3"
              :errorMessage.sync="finReportHeaderError3"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 4"
              ref="finReportHeader4"
              :inputValue.sync="finReportHeader4"
              :errorMessage.sync="finReportHeaderError4"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 5"
              ref="finReportHeader5"
              :inputValue.sync="finReportHeader5"
              :errorMessage.sync="finReportHeaderError5"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Report Header 6"
              ref="finReportHeader6"
              :inputValue.sync="finReportHeader6"
              :errorMessage.sync="finReportHeaderError6"
            />
          </BoxContainer>
          <BoxContainer title="Fin Report Header Image">
            <span
              >For the best experience, please ensure the image is designed on a <strong>200mm wide</strong> &times;
              <strong>25mm high</strong> canvas</span
            >
            <Input
              textType="file"
              placeholder="Browse"
              :isDisabled="viewMode"
              ref="finReportHeaderFileInput"
              @fileChanged="finReportHeaderImageFilesChanged"
              style="width: 50%"
            />
            <div v-if="finReportHeaderUploadedImage">
              <div>
                <strong>Uploaded Image: </strong> <span>{{ finReportHeaderUploadedImage.name }}</span>
              </div>
              <img
                :src="createBlobURL(finReportHeaderUploadedImage)"
                alt="Fin Report Header Image"
                style="height: 25mm; width: 200mm"
              />
            </div>
            <img
              v-else-if="finReportHeaderImageBlobURL"
              :src="finReportHeaderImageBlobURL"
              alt="Fin Report Header Image"
              style="height: 25mm; width: 200mm"
            />
            <div v-else class="image-placeholder">
              <div class="placeholder-text-container">
                <span>{{ finReportHeaderImagePlaceholderMessage }}</span>
              </div>
            </div>
          </BoxContainer>
          <BoxContainer class="fin-report-footer-image-box-container" title="Fin Report Footer Image">
            <span
              >For the best experience, please ensure the image is designed on a <strong>130mm wide</strong> &times;
              <strong>25mm high</strong> canvas</span
            >
            <div class="image-selector-and-uploader">
              <div class="image-selector-buttons">
                <button
                  :class="{ 'image-unselected': selectedFinReportFooterImage !== 1 }"
                  @click="finFooterImageSelectorButtonClicked(1)"
                  >Image 1</button
                >
                <button
                  :class="{ 'image-unselected': selectedFinReportFooterImage !== 2 }"
                  @click="finFooterImageSelectorButtonClicked(2)"
                  >Image 2</button
                >
                <button
                  :class="{ 'image-unselected': selectedFinReportFooterImage !== 3 }"
                  @click="finFooterImageSelectorButtonClicked(3)"
                  >Image 3</button
                >
              </div>

              <div style="margin-left: 40px; display: flex; flex-grow: 1">
                <div class="image-uploader" style="min-width: 595px">
                  <Input
                    v-if="showFinFooterFileInput"
                    textType="file"
                    placeholder="Browse"
                    :isDisabled="viewMode"
                    ref="finReportFooterFileInput"
                    @fileChanged="finReportFooterImageFilesChanged"
                    style="width: 50%"
                  />
                  <div v-if="selectedFinReportFooterUploadedImage">
                    <div>
                      <strong>Uploaded Image: </strong> <span>{{ selectedFinReportFooterUploadedImageName }}</span>
                    </div>
                    <img :src="createBlobURL(selectedFinReportFooterUploadedImage)" alt="Fin Report Footer Image" />
                  </div>
                  <img
                    v-else-if="selectedFinReportFooterImageBlobURL"
                    :src="selectedFinReportFooterImageBlobURL"
                    alt="Fin Report Footer Image"
                  />
                  <div v-else class="image-placeholder">
                    <div class="placeholder-text-container">
                      <span>{{ selectedFinReportFooterImagePlaceholderMessage }}</span>
                    </div>
                  </div>
                </div>
                <div style="margin-left: 20px; align-content: center; flex-grow: 1">
                  <Input
                    :placeholder="`Footer ${selectedFinReportFooterImage} Description`"
                    :inputValue.sync="finReportFooterDescInput"
                    @focusChanged="finReportFooterDescChanged($event, finReportFooterDescInput)"
                    validator="text,150"
                    style="width: 50%"
                    :isDisabled="viewMode"
                  />
                </div>
              </div>
            </div>
          </BoxContainer>
        </div>

        <div slot="GST Details">
          <!-- GST Details -->
          <div class="flex-row">
            <CheckBox title="GST Enabled" :isChecked.sync="gstEnabled" :isDisabled="viewMode" />
            <Input
              :isDisabled="viewMode || !gstEnabled"
              placeholder="GST License No"
              :isRequired="gstEnabled"
              validator="text,13,,,all"
              :inputValue.sync="gstLicenseNo"
              :errorMessage.sync="gstLicenseNoError"
              ref="gstLicenseNo"
            />
            <DateTimePicker
              :isDisabled="viewMode || !gstEnabled"
              placeholder="GST Effective Date"
              :value.sync="GSTEffectiveDate"
              :isRequired="gstEnabled"
              :errorMessage.sync="GSTEffectiveDateError"
              ref="gstEffectiveDate"
            />
          </div>
          <InputQuery
            :isDisabled="viewMode || !gstEnabled"
            placeholder="GST Currency"
            :isRequired="gstEnabled"
            ref="gstCurrency"
            :items="GSTCurrencyData"
            itemKey="Currency Code"
            descKey="Description"
            :search.sync="gstCurrencySearch"
            :selectedObject.sync="selectedGSTCurrency"
            :errorMessage.sync="gstCurrencyError"
          />
          <InputQuery
            :isDisabled="viewMode || !gstEnabled"
            placeholder="GST Period Category"
            :isRequired="gstEnabled"
            :search.sync="gstPeriodCategorySearch"
            ref="gstPeriodCategory"
            :items="GSTPeriodCategoryData"
            itemKey="Period Category"
            descKey="Description"
            :selectedObject.sync="selectedGSTPeriodCategory"
            :errorMessage.sync="gstPeriodCategoryError"
          />
          <DateTimePicker :isDisabled="viewMode" placeholder="GST Next Filing Date" :value.sync="GSTNextFilingDate" />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AR GL Acc No"
            ref="gstARGLAccNo"
            :items="GSTARGLAccNoData"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedGSTARGLAccNo"
            :search.sync="gstARGLAccNoSearch"
            :errorMessage.sync="gstARGLAccNoError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST AP GL Acc No"
            ref="gstAPGLAccNo"
            :items="GSTAPGLAccNoData"
            itemKey="Acc No"
            descKey="Description"
            :selectedObject.sync="selectedGSTAPGLAccNo"
            :search.sync="gstAPGLAccNoSearch"
            :errorMessage.sync="gstAPGLAccNoError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST Major Industry Code"
            ref="gstMajorIndustryCode"
            :items="MSICData"
            itemKey="MSIC Code"
            descKey="Description"
            :selectedObject.sync="selectedMSICCode"
            :allowFreeText="false"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Acc No For Deposit"
            ref="gstGLAccNoForDeposit"
            :items="GSTGLAccNoForDepositData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccNoForDepositSearch"
            :selectedObject.sync="selectedGSTGLAccNoForDeposit"
            :errorMessage.sync="gstGLAccNoForDepositError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Acc No For Bad Dept AR"
            ref="gstGLAccNoForBadDeptAR"
            :items="GSTGLAccNoForBadDeptARData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccNoForBadDeptARSearch"
            :selectedObject.sync="selectedGSTGLAccNoForBadDeptAR"
            :errorMessage.sync="gstGLAccNoForBadDeptARError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL ACC Bad Dept Recover"
            ref="gstGLAccBadDeptRecover"
            :items="GSTGLAccBadDeptRecoverData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccBadDeptRecoverSearch"
            :selectedObject.sync="selectedGSTGLAccBadDeptRecover"
            :errorMessage.sync="gstGLAccBadDeptRecoverError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL ACC Bad Dept Liability"
            ref="gstGLAccBadDeptLiability"
            :items="GSTGLAccBadDeptLiabilityData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccBadDeptLiabilitySearch"
            :selectedObject.sync="selectedGSTGLAccBadDeptLiability"
            :errorMessage.sync="gstGLAccBadDeptLiabilityError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Acc No For Bad Dept AP"
            ref="gstGLAccNoForBadDeptAP"
            :items="GSTGLAccNoForBadDeptAPData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccNoForBadDeptAPSearch"
            :selectedObject.sync="selectedGSTGLAccNoForBadDeptAP"
            :errorMessage.sync="gstGLAccNoForBadDeptAPError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Acc Bad Dept Reclaim"
            ref="gstGLAccBadDeptReclaim"
            :items="GSTGLAccBadDeptReclaimData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccBadDeptReclaimSearch"
            :selectedObject.sync="selectedGSTGLAccBadDeptReclaim"
            :errorMessage.sync="gstGLAccBadDeptReclaimError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Acc Bad Dept Claimable"
            ref="gstGLAccBadDeptClaimable"
            :items="GSTGLAccBadDeptClaimableData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLAccBadDeptClaimableSearch"
            :selectedObject.sync="selectedGSTGLAccBadDeptClaimable"
            :errorMessage.sync="gstGLAccBadDeptClaimableError"
          />
          <InputQuery
            :isDisabled="viewMode"
            placeholder="GST GL Control Acc"
            ref="gstGLControlAcc"
            :items="GSTGLControlAccData"
            itemKey="Acc No"
            descKey="Description"
            :search.sync="gstGLControlAccSearch"
            :selectedObject.sync="selectedGSTGLControlAcc"
            :errorMessage.sync="gstGLControlAccError"
          />
        </div>
      </Tab>
    </PopupEntry>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  computed: {
    selectedFinReportFooterUploadedImage: function () {
      switch (this.selectedFinReportFooterImage) {
        case 1:
          return this.finReportFooterUploadedImage1;
          break;

        case 2:
          return this.finReportFooterUploadedImage2;
          break;

        case 3:
          return this.finReportFooterUploadedImage3;
          break;
      }
    },
    selectedFinReportFooterUploadedImageName: function () {
      return this.selectedFinReportFooterUploadedImage?.name ?? '';
    },
    selectedFinReportFooterImageBlobURL: function () {
      switch (this.selectedFinReportFooterImage) {
        case 1:
          return this.finReportFooterImage1BlobURL;
          break;

        case 2:
          return this.finReportFooterImage2BlobURL;
          break;

        case 3:
          return this.finReportFooterImage3BlobURL;
          break;
      }
    },
    selectedFinReportFooterImagePlaceholderMessage: function () {
      switch (this.selectedFinReportFooterImage) {
        case 1:
          return this.finReportFooterImage1PlaceholderMessage;
          break;

        case 2:
          return this.finReportFooterImage2PlaceholderMessage;
          break;

        case 3:
          return this.finReportFooterImage3PlaceholderMessage;
          break;
      }
    },
  },
  data: function () {
    return {
      menus: [
        {
          title: 'Edit Company',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: true,
        },
        {
          title: 'View Company',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: true,
        },
      ],

      loginDetails: null,
      coFooterFin2: '',
      coFooterFin3: '',

      companyLoading: false,
      companyList: [],
      selectedCompany: [],

      popupEntryShow: false,
      popupEntryTitle: '',
      updatedBy: '',
      viewMode: false,
      entryRequired: [],
      entrySuccess: [],
      entryMessages: [],
      entryError: [],

      companyCode: '',
      companyCodeError: '',
      sstEnabled: false,
      gstEnabled: false,

      lastUpdate: '',

      tabsItems: [
        { title: 'General Details', disabled: false },
        { title: 'Other Details', disabled: false },
        { title: 'SST / GL Acc / Aging / eInvoice', disabled: false },
        { title: 'Report Header & Footer', disabled: false },
        { title: 'Fin Report Header & Footer', disabled: false },
        { title: 'GST Details', disabled: false },
      ],
      currentTab: 0,

      errorMapping: [
        { key: 'companyCode', tab: -1 },
        { key: 'co_name', tab: 0 },
        { key: 'co_sname', tab: 0 },
        { key: 'co_regno', tab: 0 },
        { key: 'co_type', tab: 0 },
        { key: 'co_addressline1', tab: 0 },
        { key: 'co_addressline2', tab: 0 },
        { key: 'co_addressline3', tab: 0 },
        { key: 'co_addressline4', tab: 0 },
        { key: 'co_city', tab: 0 },
        { key: 'co_phone1', tab: 0 },
        { key: 'co_phone2', tab: 0 },
        { key: 'co_fax1', tab: 0 },
        { key: 'co_fax2', tab: 0 },
        { key: 'co_email1', tab: 0 },
        { key: 'co_email2', tab: 0 },
        { key: 'co_businessDesc', tab: 0 },
        { key: 'co_website', tab: 1 },
        { key: 'epfNo', tab: 1 },
        { key: 'epfCategory', tab: 1 },
        { key: 'socsoNo', tab: 1 },
        { key: 'socsoCategory', tab: 1 },
        { key: 'incomeTaxNo', tab: 1 },
        { key: 'coLogoUpload', tab: 1 },
        { key: 'sstLicenseNo', tab: 2 },
        { key: 'sstSubmitter', tab: 2 },
        { key: 'sstGLAccNo', tab: 2 },
        { key: 'invoiceTIN', tab: 2 },
        { key: 'tourismTaxNo', tab: 2 },
        { key: 'plGLAccNo', tab: 2 },
        { key: 'debtorGLAccCode', tab: 2 },
        { key: 'creditorGLAccCode', tab: 2 },
        { key: 'accumulatedPLGLAccNo', tab: 2 },
        { key: 'agingType', tab: 2 },
        { key: 'monthTitle', tab: 2 },
        { key: 'financialMonth', tab: 2 },
        { key: 'agingBucket1', tab: 2 },
        { key: 'agingBucket2', tab: 2 },
        { key: 'agingBucket3', tab: 2 },
        { key: 'agingBucket4', tab: 2 },
        { key: 'agingBucket5', tab: 2 },
        { key: 'agingBucket6', tab: 2 },
        { key: 'agingBucket7', tab: 2 },
        { key: 'agingBucket8', tab: 2 },
        { key: 'agingBucket9', tab: 2 },
        { key: 'agingBucket10', tab: 2 },
        { key: 'agingBucket11', tab: 2 },
        { key: 'agingBucket12', tab: 2 },
        { key: 'defaultCreditLimit', tab: 2 },
        { key: 'defaultCreditTerm', tab: 2 },
        { key: 'fCurGainGLAcc', tab: 2 },
        { key: 'fCurLossGLAcc', tab: 2 },
        { key: 'reportHeader1', tab: 3 },
        { key: 'reportHeader2', tab: 3 },
        { key: 'reportHeader3', tab: 3 },
        { key: 'reportHeader4', tab: 3 },
        { key: 'reportHeader5', tab: 3 },
        { key: 'reportFooter1', tab: 3 },
        { key: 'reportFooter2', tab: 3 },
        { key: 'coHeaderUpload', tab: 3 },
        { key: 'finReportHeader1', tab: 4 },
        { key: 'finReportHeader2', tab: 4 },
        { key: 'finReportHeader3', tab: 4 },
        { key: 'finReportHeader4', tab: 4 },
        { key: 'finReportHeader5', tab: 4 },
        { key: 'finReportHeader6', tab: 4 },
        { key: 'coFinHeaderUpload', tab: 4 },
        { key: 'gstLicenseNo', tab: 5 },
        { key: 'gstEffectiveDate', tab: 5 },
        { key: 'gstCurrency', tab: 5 },
        { key: 'gstPeriodCategory', tab: 5 },
        { key: 'gstNextFilingDate', tab: 5 },
        { key: 'gstARGLAccNo', tab: 5 },
        { key: 'gstAPGLAccNo', tab: 5 },
        { key: 'gstMajorIndustryCode', tab: 5 },
        { key: 'gstGLAccNoForDeposit', tab: 5 },
        { key: 'gstGLAccNoForBadDeptAR', tab: 5 },
        { key: 'gstGLAccBadDeptRecover', tab: 5 },
        { key: 'gstGLAccBadDeptLiability', tab: 5 },
        { key: 'gstGLAccNoForBadDeptAP', tab: 5 },
        { key: 'gstGLAccBadDeptReclaim', tab: 5 },
        { key: 'gstGLAccBadDeptClaimable', tab: 5 },
        { key: 'gstGLControlAcc', tab: 5 },
      ],

      //#region General Details data
      co_name: '',
      nameError: '',
      co_sname: '',
      snameError: '',
      regNo: '',
      regNoError: '',
      establishDate: null,
      CoTypeList: [],
      coTypeSearch: '',
      selectedCoType: null,
      coTypeError: '',
      addressData: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
      },
      addressRequired: {
        addressLine1: true,
        addressLine2: false,
        addressLine3: false,
        addressLine4: false,
        postcode: true,
        city: false,
        state: false,
        country: false,
      },
      postcodeList: [],
      stateList: [],
      countryList: [],
      addressErrors: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
      },
      phone1: '',
      phone1Error: '',
      phone2: '',
      phone2Error: '',
      co_fax1: '',
      faxError1: '',
      co_fax2: '',
      faxError2: '',
      co_email1: '',
      emailError1: '',
      co_email2: '',
      emailError2: '',
      co_businessDesc: '',
      businessDescError: '',

      //#region Other Details data
      co_website: '',
      websiteError: '',
      epfNo: '',
      epfNoError: '',
      EPFCategoryData: [],
      epfCategorySearch: '',
      selectedEPFCategory: null,
      epfCategoryError: '',
      socsoNo: '',
      socsoNoError: '',
      SOCSOCategoryData: [],
      socsoCategorySearch: '',
      selectedSOCSOCategory: null,
      socsoCategoryError: '',
      incomeTaxNo: '',
      incomeTaxNoError: '',

      companyLogoUploadedImage: null, // newly uploaded image from the user, stores a file object
      companyLogoBlobURL: '', // the original image from the API
      companyLogoPlaceholderMessage: '', // message to display if the original image could not be fetched for some reason
      //endregion

      //#region SST / GL Acc / Aging / eInvoice
      SSTEffectiveDate: null,
      sstLicenseNo: '',
      sstLicenseNoError: '',
      SSTSubmitterData: [],
      sstSubmitterSearch: '',
      selectedSSTSubmitter: null,
      sstSubmitterError: '',
      sstValidationButtons: [
        { label: 'Yes', value: '1' },
        { label: 'No', value: '0' },
      ],
      sstValidationValue: null,
      sstServiceTypeCode: '',
      SSTGLAccNoData: [],
      sstGLAccSearch: '',
      selectedSSTGLAccNo: null,
      sstGLAccNoError: '',
      invoiceTIN: '',
      invoiceTINError: '',
      tourismTaxNo: '',
      tourismTaxNoError: '',
      version: '',
      PLGLAccNoData: [],
      plGLAccSearch: '',
      selectedPLGLAccNo: null,
      plGLAccNoError: '',
      AccumulatedPLGLAccNoData: [],
      accumulatedPLGLAccSearch: '',
      selectedAccumulatedPLGLAccNo: null,
      accumulatedPLGLAccNoError: '',
      DebtorGLAccCodeData: [],
      debtorGLAccSearch: '',
      selectedDebtorGLAccCode: null,
      debtorGLAccCodeError: '',
      CreditorGLAccCodeData: [],
      creditorGLAccSearch: '',
      selectedCreditorGLAccCode: null,
      creditorGLAccCodeError: '',
      AgingTypeList: [],
      agingTypeSearch: '',
      selectedAgingType: null,
      agingTypeError: '',
      MonthTitleList: [],
      monthTitleSearch: '',
      monthTitleIsDisabled: true,
      selectedMonthTitle: null,
      monthTitleError: '',
      selectedFinancialMonth: null,
      financialMonthSearch: '',
      financialMonthError: '',
      agingBucket1: '',
      agingBucketsError1: '',
      agingBucket2: '',
      agingBucketsError2: '',
      agingBucket3: '',
      agingBucketsError3: '',
      agingBucket4: '',
      agingBucketsError4: '',
      agingBucket5: '',
      agingBucketsError5: '',
      agingBucket6: '',
      agingBucketsError6: '',
      agingBucket7: '',
      agingBucketsError7: '',
      agingBucket8: '',
      agingBucketsError8: '',
      agingBucket9: '',
      agingBucketsError9: '',
      agingBucket10: '',
      agingBucketsError10: '',
      agingBucket11: '',
      agingBucketsError11: '',
      agingBucket12: '',
      agingBucketsError12: '',
      defaultCreditLimit: '',
      defaultCreditLimitError: '',
      defaultCreditTerm: '',
      defaultCreditTermError: '',
      FCurGainGLAccData: [],
      fCurGainGLAccSearch: '',
      selectedFCurGainGLAcc: null,
      fCurGainGLAccError: '',
      FCurLossGLAccData: [],
      fCurLossGLAccSearch: '',
      selectedFCurLossGLAcc: null,
      fCurLossGLAccError: '',
      //#endregion

      //#region Report Header & Footer
      reportHeader1: '',
      reportHeaderError1: '',
      reportHeader2: '',
      reportHeaderError2: '',
      reportHeader3: '',
      reportHeaderError3: '',
      reportHeader4: '',
      reportHeaderError4: '',
      reportHeader5: '',
      reportHeaderError5: '',
      reportFooter1: '',
      reportFooterError1: '',
      reportFooter2: '',
      reportFooterError2: '',

      reportHeaderUploadedImage: null, // newly uploaded image from the user, stores a file object
      reportHeaderImageBlobURL: '', // the original image from the API
      reportHeaderImagePlaceholderMessage: '', // message to display if the original image could not be fetched for some reason
      //#endregion

      //#region Fin Report Header & Footer
      finReportHeader1: '',
      finReportHeaderError1: '',
      finReportHeader2: '',
      finReportHeaderError2: '',
      finReportHeader3: '',
      finReportHeaderError3: '',
      finReportHeader4: '',
      finReportHeaderError4: '',
      finReportHeader5: '',
      finReportHeaderError5: '',
      finReportHeader6: '',
      finReportHeaderError6: '',

      finReportHeaderUploadedImage: null, // newly uploaded image from the user, stores a file object
      finReportHeaderImageBlobURL: '', // the original image from the API
      finReportHeaderImagePlaceholderMessage: '', // message to display if the original image could not be fetched for some reason

      selectedFinReportFooterImage: 1, // image selector state
      showFinFooterFileInput: true, // used to force the file input to re-render and clear off files list

      finReportFooterUploadedImage1: null, // newly uploaded images from the user
      finReportFooterUploadedImage2: null, // stores file objects
      finReportFooterUploadedImage3: null,

      finReportFooterDescInput: '',
      finReportFooterDesc1: '', // Footer description that will be shown in financial report footer selection
      finReportFooterDesc2: '',
      finReportFooterDesc3: '',

      finReportFooterImage1BlobURL: '', // the original image from the API
      finReportFooterImage2BlobURL: '',
      finReportFooterImage3BlobURL: '',

      finReportFooterImage1PlaceholderMessage: '', // message to display if the original image could not be fetched for some reason
      finReportFooterImage2PlaceholderMessage: '',
      finReportFooterImage3PlaceholderMessage: '',
      //#endregion

      //#region GST Details
      gstLicenseNo: '',
      gstLicenseNoError: '',
      GSTEffectiveDate: null,
      GSTEffectiveDateError: '',
      GSTCurrencyData: [],
      gstCurrencySearch: '',
      selectedGSTCurrency: null,
      gstCurrencyError: '',
      GSTPeriodCategoryData: [],
      gstPeriodCategorySearch: '',
      selectedGSTPeriodCategory: null,
      gstPeriodCategoryError: '',
      GSTNextFilingDate: null,
      GSTARGLAccNoData: [],
      gstARGLAccNoSearch: '',
      selectedGSTARGLAccNo: null,
      gstARGLAccNoError: '',
      GSTAPGLAccNoData: [],
      gstAPGLAccNoSearch: '',
      selectedGSTAPGLAccNo: null,
      gstAPGLAccNoError: '',
      MSICData: [],
      selectedMSICCode: null,
      GSTGLAccNoForDepositData: [],
      gstGLAccNoForDepositSearch: '',
      selectedGSTGLAccNoForDeposit: null,
      gstGLAccNoForDepositError: '',
      GSTGLAccNoForBadDeptARData: [],
      gstGLAccNoForBadDeptARSearch: '',
      selectedGSTGLAccNoForBadDeptAR: null,
      gstGLAccNoForBadDeptARError: '',
      GSTGLAccBadDeptRecoverData: [],
      gstGLAccBadDeptRecoverSearch: '',
      selectedGSTGLAccBadDeptRecover: null,
      gstGLAccBadDeptRecoverError: '',
      GSTGLAccBadDeptLiabilityData: [],
      gstGLAccBadDeptLiabilitySearch: '',
      selectedGSTGLAccBadDeptLiability: null,
      gstGLAccBadDeptLiabilityError: '',
      GSTGLAccNoForBadDeptAPData: [],
      gstGLAccNoForBadDeptAPSearch: '',
      selectedGSTGLAccNoForBadDeptAP: null,
      gstGLAccNoForBadDeptAPError: '',
      GSTGLAccBadDeptReclaimData: [],
      gstGLAccBadDeptReclaimSearch: '',
      selectedGSTGLAccBadDeptReclaim: null,
      gstGLAccBadDeptReclaimError: '',
      GSTGLAccBadDeptClaimableData: [],
      gstGLAccBadDeptClaimableSearch: '',
      selectedGSTGLAccBadDeptClaimable: null,
      gstGLAccBadDeptClaimableError: '',
      GSTGLControlAccData: [],
      gstGLControlAccSearch: '',
      selectedGSTGLControlAcc: null,
      gstGLControlAccError: '',
      //#endregion
      monthList: [
        { month: 'January', integer: '1' },
        { month: 'February', integer: '2' },
        { month: 'March', integer: '3' },
        { month: 'April', integer: '4' },
        { month: 'May', integer: '5' },
        { month: 'June', integer: '6' },
        { month: 'July', integer: '7' },
        { month: 'August', integer: '8' },
        { month: 'September', integer: '9' },
        { month: 'October', integer: '10' },
        { month: 'November', integer: '11' },
        { month: 'December', integer: '12' },
      ],
    };
  },
  props: {},
  methods: {
    fetchImage: async function (imageURL) {
      // used to ignore the browser's cache and get the image
      return fetch(
        imageURL,
        {
          cache: 'reload', // https://developer.mozilla.org/en-US/docs/Web/API/fetch#cache
        }, // if this option isn't passed, the browser may return the image from the cache instead of always calling the API
      )
        .then(async (response) => {
          if (response.ok) {
            // image successfully fetched
            return response.blob().then((blob) => URL.createObjectURL(blob)); // create and return blob URL to display the fetched image
          }

          if (response.status === 404) {
            // image was not found in the API
            return 'notfound';
          }

          return 'error'; // for all other scenarios, assume an error occurred
        })
        .catch((error) => {
          return 'error'; // undefined indicates an error occurred
        });
    },
    clearAllAgingBuckets: function () {
      this.agingBucket1 = '';
      this.agingBucket2 = '';
      this.agingBucket3 = '';
      this.agingBucket4 = '';
      this.agingBucket5 = '';
      this.agingBucket6 = '';
      this.agingBucket7 = '';
      this.agingBucket8 = '';
      this.agingBucket9 = '';
      this.agingBucket10 = '';
      this.agingBucket11 = '';
      this.agingBucket12 = '';
    },
    agingBucket1InputChanged: function (newValue, oldValue) {
      if (isNaN(newValue)) {
        this.clearAllAgingBuckets();
        return;
      }

      const parsedNewValue = parseInt(newValue);
      this.agingBucket2 = (parsedNewValue * 2).toString();
      this.agingBucket3 = (parsedNewValue * 3).toString();
      this.agingBucket4 = (parsedNewValue * 4).toString();
      this.agingBucket5 = (parsedNewValue * 5).toString();
      this.agingBucket6 = (parsedNewValue * 6).toString();
      this.agingBucket7 = (parsedNewValue * 7).toString();
      this.agingBucket8 = (parsedNewValue * 8).toString();
      this.agingBucket9 = (parsedNewValue * 9).toString();
      this.agingBucket10 = (parsedNewValue * 10).toString();
      this.agingBucket11 = (parsedNewValue * 11).toString();
      this.agingBucket12 = (parsedNewValue * 12).toString();
    },
    agingType: function (selectedAgingType) {
      if (selectedAgingType['Aging Type'] === 'M') {
        this.monthTitleIsDisabled = false;
        this.agingBucket1 = '0';
        this.agingBucket2 = '1';
        this.agingBucket3 = '2';
        this.agingBucket4 = '3';
        this.agingBucket5 = '4';
        this.agingBucket6 = '5';
        this.agingBucket7 = '6';
        this.agingBucket8 = '7';
        this.agingBucket9 = '8';
        this.agingBucket10 = '9';
        this.agingBucket11 = '10';
        this.agingBucket12 = '11';
      } else {
        this.monthTitleIsDisabled = true;
        this.selectedMonthTitle = null;
      }
    },
    menuClicked: async function (e) {
      this.clearEntryMessages();
      this.clearTabFields();

      switch (e['title']) {
        case 'Edit Company':
          if (this.selectedCompany.length == 0) {
            this.$showAlert('error', 'No Company Selected', 'Please select a company to edit');
          } else {
            this.popupEntryShow = true;
            this.popupEntryTitle = 'Edit Company';

            await this.populateCompanyDetails(this.selectedCompany[0]['Company Code']);
          }
          break;
        case 'View Company':
          if (this.selectedCompany.length == 0) {
            this.$showAlert('error', 'No Company Selected', 'Please select a company to view');
          } else {
            this.viewMode = true;
            this.popupEntryShow = true;
            this.popupEntryTitle = 'View Company Details';

            await this.populateCompanyDetails(this.selectedCompany[0]['Company Code']);
          }
          break;
      }
    },

    getCompany: async function () {
      this.$fullscreenLoading('Getting Companies');
      this.companyList = [];
      this.companyList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'COMPANY' });
      this.$fullscreenLoading();
    },

    getCoType: async function () {
      this.$fullscreenLoading('Getting Company Type');
      this.CoTypeList = [];
      this.CoTypeList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'COMPANYTYPE' });
      this.$fullscreenLoading();
    },

    getEPFCategoryDefault: async function () {
      this.$fullscreenLoading('Getting EPF Category');
      this.EPFCategoryData = [];
      this.EPFCategoryData = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'EPFCATEGORY' });
      this.$fullscreenLoading();
    },

    getSOCSOCategoryDefault: async function () {
      this.$fullscreenLoading('Getting SOCSO Category');
      this.SOCSOCategoryData = [];
      this.SOCSOCategoryData = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'SOCSOCATEGORY' });
      this.$fullscreenLoading();
    },

    getAgingType: async function () {
      this.$fullscreenLoading('Getting Aging Type');
      this.AgingTypeList = [];
      this.AgingTypeList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'AGINGTYPE' });
      this.$fullscreenLoading();
    },

    getMonthTitle: async function () {
      this.$fullscreenLoading('Getting Month Title');
      this.MonthTitleList = [];
      this.MonthTitleList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'SOA_MTHNAME' });
      this.$fullscreenLoading();
    },

    companyLogoFilesChanged: async function (files) {
      if (files.length === 0) {
        // will be 0 if user clicks on "clear selected files"
        this.companyLogoUploadedImage = null;
        return;
      }

      const uploadedFile = files[0];
      if (uploadedFile.name.split('.').pop().toLowerCase() !== 'png') {
        // only allow png files
        this.$showAlert('error', 'Image Type Not Supported', 'Only .png images are supported', [{ text: 'OK' }]);
        this.$refs.companyLogoFileInput.clearAttachmentClicked();
        return;
      }

      this.companyLogoUploadedImage = uploadedFile;
    },

    reportHeaderImageFilesChanged: async function (files) {
      if (files.length === 0) {
        // will be 0 if user clicks on "clear selected files"
        this.reportHeaderUploadedImage = null;
        return;
      }

      const uploadedFile = files[0];
      if (uploadedFile.name.split('.').pop().toLowerCase() !== 'png') {
        // only allow png files
        this.$showAlert('error', 'Image Type Not Supported', 'Only .png images are supported', [{ text: 'OK' }]);
        this.$refs.reportHeaderFileInput.clearAttachmentClicked();
        return;
      }

      if (!(await this.aspectRatioWithinAcceptableRange(uploadedFile, 200 / 25))) {
        this.$showAlert(
          'error',
          'Improper Image Size',
          'Please ensure the image is designed on a<br><strong>200mm wide</strong> &times; <strong>25mm high</strong> canvas',
          [{ text: 'OK' }],
        );
        this.$refs.reportHeaderFileInput.clearAttachmentClicked();
        return;
      }

      this.reportHeaderUploadedImage = uploadedFile;
    },

    finReportHeaderImageFilesChanged: async function (files) {
      if (files.length === 0) {
        // will be 0 if user clicks on "clear selected files"
        this.finReportHeaderUploadedImage = null;
        return;
      }

      const uploadedFile = files[0];
      if (uploadedFile.name.split('.').pop().toLowerCase() !== 'png') {
        // only allow png files
        this.$showAlert('error', 'Image Type Not Supported', 'Only .png images are supported', [{ text: 'OK' }]);
        this.$refs.finReportHeaderFileInput.clearAttachmentClicked();
        return;
      }

      if (!(await this.aspectRatioWithinAcceptableRange(uploadedFile, 200 / 25))) {
        this.$showAlert(
          'error',
          'Improper Image Size',
          'Please ensure the image is designed on a<br><strong>200mm wide</strong> &times; <strong>25mm high</strong> canvas',
          [{ text: 'OK' }],
        );
        this.$refs.finReportHeaderFileInput.clearAttachmentClicked();
        return;
      }

      this.finReportHeaderUploadedImage = uploadedFile;
    },

    finFooterImageSelectorButtonClicked: function (selectedImage) {
      this.selectedFinReportFooterImage = selectedImage;
      switch (selectedImage) {
        case 1:
          this.finReportFooterDescInput = this.finReportFooterDesc1;
          break;

        case 2:
          this.finReportFooterDescInput = this.finReportFooterDesc2;
          break;

        case 3:
          this.finReportFooterDescInput = this.finReportFooterDesc3;
          break;
      }
      this.showFinFooterFileInput = false; // can't use clearAttachmentClicked here
      this.$nextTick(() => {
        // because that will emit the fileChanged event on the file input, which will then trigger the event handler and clear off the uploaded image erroneously
        this.showFinFooterFileInput = true; // use a data member to force the file input to re-render instead, which will also clear off the attachments list, but won't emit the fileChanged event
      });
    },
    finReportFooterImageFilesChanged: async function (files) {
      if (files.length === 0) {
        // will be 0 if user clicks on "clear selected files"
        switch (this.selectedFinReportFooterImage) {
          case 1:
            this.finReportFooterUploadedImage1 = null;
            break;

          case 2:
            this.finReportFooterUploadedImage2 = null;
            break;

          case 3:
            this.finReportFooterUploadedImage3 = null;
            break;
        }

        return;
      }

      const uploadedFile = files[0];
      if (uploadedFile.name.split('.').pop().toLowerCase() !== 'png') {
        // only allow png files
        this.$showAlert('error', 'Image Type Not Supported', 'Only .png images are supported', [{ text: 'OK' }]);
        this.$refs.finReportFooterFileInput.clearAttachmentClicked();
        return;
      }

      if (!(await this.aspectRatioWithinAcceptableRange(uploadedFile, 130 / 25))) {
        this.$showAlert(
          'error',
          'Improper Image Size',
          'Please ensure the image is designed on a<br><strong>130mm wide</strong> &times; <strong>25mm high</strong> canvas',
          [{ text: 'OK' }],
        );
        this.$refs.finReportFooterFileInput.clearAttachmentClicked();
        return;
      }

      switch (this.selectedFinReportFooterImage) {
        case 1:
          this.finReportFooterUploadedImage1 = uploadedFile;
          break;

        case 2:
          this.finReportFooterUploadedImage2 = uploadedFile;
          break;

        case 3:
          this.finReportFooterUploadedImage3 = uploadedFile;
          break;
      }
    },

    finReportFooterDescChanged: async function (focus, desc) {
      if (!focus) {
        switch (this.selectedFinReportFooterImage) {
          case 1:
            this.finReportFooterDesc1 = desc;
            break;

          case 2:
            this.finReportFooterDesc2 = desc;
            break;

          case 3:
            this.finReportFooterDesc3 = desc;
            break;
        }
      }
    },

    createBlobURL: function (file) {
      return URL.createObjectURL(file);
    },

    aspectRatioWithinAcceptableRange: async function (imageFile, targetAspectRatio) {
      const blobURL = this.createBlobURL(imageFile);
      const imageDimensions = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          resolve({
            height: image.naturalHeight,
            width: image.naturalWidth,
          });
        };

        image.src = blobURL;
      });

      const lowerLimit = targetAspectRatio - 0.2; // allow a little margin for error instead of strictly forcing it to a specific aspect ratio
      const upperLimit = targetAspectRatio + 0.2;
      const imageAspectRatio = imageDimensions.width / imageDimensions.height;
      return imageAspectRatio >= lowerLimit && imageAspectRatio <= upperLimit;
    },

    populateCompanyDetails: async function (company) {
      this.$fullscreenLoading('Getting Details');
      const details = (
        await this.$apiCallV2('/Master/GetDetailsMaster', {
          masterfile: 'COMPANY',
          module: 'MASTER',
          recordid: company,
        })
      )['Company'];

      this.companyCode = this.populateTextTrim(details['Company_code']);
      this.sstEnabled = details['CompanyGSTEnabled'] == '1';
      this.gstEnabled = details['CompanyGST2Enabled'] == '1';

      this.lastUpdate = details['DateUpdate'];
      this.updatedBy = `${details['UserID'] || ''} ${details['DateUpdate'] ? new Date(details['DateUpdate']).formatString('dd/MM/yyyy HH:mm') : ''}`;

      //#region populate General Details tab
      this.co_name = this.populateTextTrim(details['Company_name']);
      this.co_sname = this.populateTextTrim(details['Company_short_name']);
      this.regNo = this.populateTextTrim(details['Company_Register_Number']);
      this.establishDate = details['CompanyDate'] ? new Date(details['CompanyDate']) : null; // Need to focus if there is data
      this.selectedCoType =
        details['Parent'] ? this.populateObject(this.CoTypeList, 'Company Type', details['Parent']) : null;
      this.addressData['addressLine1'] = this.populateTextTrim(details['CompanyAddress1']);
      this.addressData['addressLine2'] = this.populateTextTrim(details['CompanyAddress2']);
      this.addressData['addressLine3'] = this.populateTextTrim(details['CompanyAddress3']);
      this.addressData['addressLine4'] = this.populateTextTrim(details['CompanyAddress4']);
      this.addressData['postcode'] = this.populateTextTrim(details['CompanyPostCode']);
      this.addressData['city'] = this.populateTextTrim(details['CompanyCity']);
      this.addressData['state'] = this.populateTextTrim(details['CompanyState']);
      this.addressData['country'] = this.populateTextTrim(details['CompanyCountry']);
      this.phone1 = this.populateTextTrim(details['CompanyTelNo']);
      this.phone2 = this.populateTextTrim(details['CompanyTelNo2']);
      this.co_fax1 = this.populateTextTrim(details['CompanyFaxNo']);
      this.co_fax2 = this.populateTextTrim(details['CompanyFaxNo2']);
      this.co_email1 = this.populateTextTrim(details['CompanyEmail']);
      this.co_email2 = this.populateTextTrim(details['CompanyEmail2']);
      this.co_businessDesc = this.populateTextTrim(details['CompanyBusinessDesc']);
      //#endregion

      //#region populate Other Details tab
      this.co_website = this.populateTextTrim(details['CompanyURL']);
      this.epfNo = this.populateTextTrim(details['CompanyEPFNo']);
      this.selectedEPFCategory =
        details['EPFCategory'] ?
          this.populateObject(this.EPFCategoryData, 'EPFCategory', details['EPFCategory'])
        : null;
      this.socsoNo = this.populateTextTrim(details['CompanySOCSO']);
      this.selectedSOCSOCategory =
        details['SOCSOCategory'] ?
          this.populateObject(this.SOCSOCategoryData, 'SocsoCategory', details['SOCSOCategory'])
        : null;
      this.incomeTaxNo = this.populateTextTrim(details['CompamyIncomeTax']);

      if (this.loginDetails.CoLogo) {
        const fetchedImageBlobURL = await this.fetchImage(this.loginDetails.CoLogo);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.companyLogoBlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.companyLogoPlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.companyLogoPlaceholderMessage = 'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.companyLogoPlaceholderMessage = 'URL missing. Please relogin and try again.';
      }
      //#endregion

      //region populate SST / GL Acc / Aging / eInvoice tab
      this.SSTEffectiveDate = details['CompanyGSTEffectiveDate'] ? new Date(details['CompanyGSTEffectiveDate']) : null; // Need to focus if there is data
      this.sstLicenseNo = this.populateTextTrim(details['CompanyGSTLicense']);
      this.selectedSSTSubmitter = this.populateObject(this.SSTSubmitterData, 'EmployeeID', details['GST']);
      this.selectedSSTGLAccNo = this.populateObject(this.SSTGLAccNoData, 'Acc No', details['CompanyGSTGLAcc']);
      this.sstValidationValue = this.sstValidationButtons.find((v) => v.value == (details['GSTValidateAcc'] || '0'));
      this.sstServiceTypeCode = details['ServiceTypeCode'] || '';
      this.invoiceTIN = this.populateTextTrim(details['CompanyTIN']);
      this.tourismTaxNo = this.populateTextTrim(details['CompanyTourismTaxNo']);
      this.version = this.populateTextTrim(details['CompanyeInvoiceVersion']);
      this.selectedPLGLAccNo = this.populateObject(this.PLGLAccNoData, 'Acc No', details['PNLAcc']);
      this.selectedAccumulatedPLGLAccNo = this.populateObject(
        this.AccumulatedPLGLAccNoData,
        'Acc No',
        details['AccPNLAcc'],
      );
      this.selectedDebtorGLAccCode = this.populateObject(this.DebtorGLAccCodeData, 'Acc No', details['DebtorCtrlAcc']);
      this.selectedCreditorGLAccCode = this.populateObject(
        this.CreditorGLAccCodeData,
        'Acc No',
        details['CreditorCtrlAcc'],
      );
      this.selectedAgingType =
        details['AgeingType'] ? this.populateObject(this.AgingTypeList, 'Aging Type', details['AgeingType']) : null;
      this.selectedMonthTitle =
        details['Month Title'] ? this.populateObject(this.MonthTitleList, 'Code', details['Month Title']) : null;
      this.selectedFinancialMonth =
        details['FinancialMth'] ? this.populateObject(this.monthList, 'integer', details['FinancialMth']) : null;
      this.financialMonth = this.populateTextTrim(details['FinancialMth']);
      this.agingBucket1 = this.populateTextTrim(details['CompanyDuration1']);
      this.agingBucket2 = this.populateTextTrim(details['CompanyDuration2']);
      this.agingBucket3 = this.populateTextTrim(details['CompanyDuration3']);
      this.agingBucket4 = this.populateTextTrim(details['CompanyDuration4']);
      this.agingBucket5 = this.populateTextTrim(details['CompanyDuration5']);
      this.agingBucket6 = this.populateTextTrim(details['CompanyDuration6']);
      this.agingBucket7 = this.populateTextTrim(details['CompanyDuration7']);
      this.agingBucket8 = this.populateTextTrim(details['CompanyDuration8']);
      this.agingBucket9 = this.populateTextTrim(details['CompanyDuration9']);
      this.agingBucket10 = this.populateTextTrim(details['CompanyDuration10']);
      this.agingBucket11 = this.populateTextTrim(details['CompanyDuration11']);
      this.agingBucket12 = this.populateTextTrim(details['CompanyDuration12']);
      this.defaultCreditLimit = this.populateTextTrim(details['DefaultCRLimit']);
      this.defaultCreditTerm = this.populateTextTrim(details['DefaultCreditTerm']);
      this.selectedFCurGainGLAcc = this.populateObject(this.FCurGainGLAccData, 'Acc No', details['EXCHANGEGAIN']);
      this.selectedFCurLossGLAcc = this.populateObject(this.FCurLossGLAccData, 'Acc No', details['EXCHANGELOSS']);
      //#endregion

      //#region populate Report Header & Footer tab
      this.reportHeader1 = this.populateTextTrim(details['Rpt Header1']);
      this.reportHeader2 = this.populateTextTrim(details['Rpt Header2']);
      this.reportHeader3 = this.populateTextTrim(details['Rpt Header3']);
      this.reportHeader4 = this.populateTextTrim(details['Rpt Header4']);
      this.reportHeader5 = this.populateTextTrim(details['Rpt Header5']);
      this.reportFooter1 = this.populateTextTrim(details['ReportFtrLine1']);
      this.reportFooter2 = this.populateTextTrim(details['ReportFtrLine2']);

      if (this.loginDetails.CoHeaderOpr) {
        const fetchedImageBlobURL = await this.fetchImage(this.loginDetails.CoHeaderOpr);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.reportHeaderImageBlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.reportHeaderImagePlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.reportHeaderImagePlaceholderMessage = 'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.reportHeaderImagePlaceholderMessage = 'URL missing. Please relogin and try again.';
      }
      //#endregion

      //#region populate Fin Report Header & Footer tab
      this.finReportHeader1 = this.populateTextTrim(details['Fin Rpt Header1']);
      this.finReportHeader2 = this.populateTextTrim(details['Fin Rpt Header2']);
      this.finReportHeader3 = this.populateTextTrim(details['Fin Rpt Header3']);
      this.finReportHeader4 = this.populateTextTrim(details['Fin Rpt Header4']);
      this.finReportHeader5 = this.populateTextTrim(details['Fin Rpt Header5']);
      this.finReportHeader6 = this.populateTextTrim(details['Fin Rpt Header6']);

      if (this.loginDetails.CoHeaderFin) {
        const fetchedImageBlobURL = await this.fetchImage(this.loginDetails.CoHeaderFin);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.finReportHeaderImageBlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.finReportHeaderImagePlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.finReportHeaderImagePlaceholderMessage = 'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.finReportHeaderImagePlaceholderMessage = 'URL missing. Please relogin and try again.';
      }

      if (this.loginDetails.CoFooterFin) {
        const fetchedImageBlobURL = await this.fetchImage(this.loginDetails.CoFooterFin);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.finReportFooterImage1BlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.finReportFooterImage1PlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.finReportFooterImage1PlaceholderMessage =
                'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.finReportFooterImage1PlaceholderMessage = 'URL missing. Please relogin and try again.';
      }

      if (this.coFooterFin2) {
        const fetchedImageBlobURL = await this.fetchImage(this.coFooterFin2);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.finReportFooterImage2BlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.finReportFooterImage2PlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.finReportFooterImage2PlaceholderMessage =
                'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.finReportFooterImage2PlaceholderMessage = 'URL missing. Please refresh the page and try again.';
      }

      if (this.coFooterFin3) {
        const fetchedImageBlobURL = await this.fetchImage(this.coFooterFin3);
        if (fetchedImageBlobURL.startsWith('blob:')) {
          this.finReportFooterImage3BlobURL = fetchedImageBlobURL;
        } else {
          switch (fetchedImageBlobURL) {
            case 'notfound':
              this.finReportFooterImage3PlaceholderMessage = 'Image not found';
              break;

            case 'error':
            default:
              this.finReportFooterImage3PlaceholderMessage =
                'An error occurred. Please refresh the page and try again.';
              break;
          }
        }
      } else {
        this.finReportFooterImage3PlaceholderMessage = 'URL missing. Please refresh the page and try again.';
      }

      this.finReportFooterDesc1 = (
        await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc1')
      )[0].SettingsValue;
      this.finReportFooterDesc2 = (
        await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc2')
      )[0].SettingsValue;
      this.finReportFooterDesc3 = (
        await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc3')
      )[0].SettingsValue;
      this.finReportFooterDescInput = this.finReportFooterDesc1;
      // #endregion

      //#region populate GST Details tab
      this.gstLicenseNo = this.populateTextTrim(details['CompanyGST2License']);
      this.GSTEffectiveDate =
        details['CompanyGST2EffectiveDate'] ? new Date(details['CompanyGST2EffectiveDate']) : null;
      this.selectedGSTCurrency = this.populateObject(
        this.GSTCurrencyData,
        'Currency Code',
        details['CompanyGST2ReportingCurrency'],
      );
      this.selectedGSTPeriodCategory = this.populateObject(
        this.GSTPeriodCategoryData,
        'Period Category',
        details['CompanyGST2TaxablePeriodCategory'],
      );
      this.GSTNextFilingDate =
        details['CompanyGST2NextFilingDate'] ? new Date(details['CompanyGST2NextFilingDate']) : null;
      this.selectedGSTARGLAccNo = this.populateObject(this.GSTARGLAccNoData, 'Acc No', details['CompanyGST2ARGLAcc']);
      this.selectedGSTAPGLAccNo = this.populateObject(this.GSTAPGLAccNoData, 'Acc No', details['CompanyGST2APGLAcc']);
      this.gstMajorIndustryCode = this.populateTextTrim(details['CompanyGST2MajorIndustryCode']);
      this.selectedMSICCode = this.populateObject(this.MSICData, 'MSIC Code', details['CompanyGST2MajorIndustryCode']);
      this.selectedGSTGLAccNoForDeposit = this.populateObject(
        this.GSTGLAccNoForDepositData,
        'Acc No',
        details['CompanyGST2SuspendonDepositARGLAcc'],
      );
      this.selectedGSTGLAccNoForBadDeptAR = this.populateObject(
        this.GSTGLAccNoForBadDeptARData,
        'Acc No',
        details['CompanyGST2BadDebtARGLAcc'],
      );
      this.selectedGSTGLAccBadDeptRecover = this.populateObject(
        this.GSTGLAccBadDeptRecoverData,
        'Acc No',
        details['CompanyGST2BadDebtRecoveredARGLAcc'],
      );
      this.selectedGSTGLAccBadDeptLiability = this.populateObject(
        this.GSTGLAccBadDeptLiabilityData,
        'Acc No',
        details['CompanyGST2BadDebtARLiability'],
      );
      this.selectedGSTGLAccNoForBadDeptAP = this.populateObject(
        this.GSTGLAccNoForBadDeptAPData,
        'Acc No',
        details['CompanyGST2BadDebtAPGLAcc'],
      );
      this.selectedGSTGLAccBadDeptReclaim = this.populateObject(
        this.GSTGLAccBadDeptReclaimData,
        'Acc No',
        details['CompanyGST2BadDebtPaidAPGLAcc'],
      );
      this.selectedGSTGLAccBadDeptClaimable = this.populateObject(
        this.GSTGLAccBadDeptClaimableData,
        'Acc No',
        details['CompanyGST2BadDebtAPClaimable'],
      );
      this.selectedGSTGLControlAcc = this.populateObject(
        this.GSTGLControlAccData,
        'Acc No',
        details['CompanyGST2ControlGLAcc'],
      );
      //#endregion

      this.$fullscreenLoading();
    },
    clearTabFields: function () {
      this.viewMode = false;

      this.currentTab = 0;

      this.companyCode = '';
      this.companyCodeError = '';
      this.sstEnabled = false;
      this.gstEnabled = false;

      this.lastUpdate = '';

      this.attachment = null;

      //#region General Details data
      this.co_name = '';
      this.nameError = '';
      this.co_sname = '';
      this.snameError = '';
      this.regNo = '';
      this.regNoError = '';
      this.establishDate = null;
      this.selectedCoType = null;
      this.coTypeSearch = '';
      this.coTypeError = '';
      this.addressData['addressLine1'] = '';
      this.addressData['addressLine2'] = '';
      this.addressData['addressLine3'] = '';
      this.addressData['addressLine4'] = '';
      this.addressData['postcode'] = '';
      this.addressData['city'] = '';
      this.addressData['state'] = '';
      this.addressData['country'] = '';
      this.addressErrors['addressLine1'] = '';
      this.addressErrors['addressLine2'] = '';
      this.addressErrors['addressLine3'] = '';
      this.addressErrors['addressLine4'] = '';
      this.addressErrors['city'] = '';
      this.phone1 = '';
      this.phone1Error = '';
      this.phone2 = '';
      this.phone2Error = '';
      this.co_fax1 = '';
      this.faxError1 = '';
      this.co_fax2 = '';
      this.faxError2 = '';
      this.co_email1 = '';
      this.emailError1 = '';
      this.co_email2 = '';
      this.emailError2 = '';
      this.co_businessDesc = '';
      this.businessDescError = '';
      //#endregion

      //#region Other Details data
      this.co_website = '';
      this.websiteError = '';
      this.epfNo = '';
      this.epfNoError = '';
      this.selectedEPFCategory = null;
      this.epfCategorySearch = '';
      this.epfCategoryError = '';
      this.socsoNo = '';
      this.socsoNoError = '';
      this.selectedSOCSOCategory = null;
      this.socsoCategorySearch = '';
      this.socsoCategoryError = '';
      this.incomeTaxNo = '';
      this.incomeTaxNoError = '';

      this.companyLogoUploadedImage = null;
      this.companyLogoBlobURL = '';
      this.companyLogoPlaceholderMessage = '';
      //endregion

      //#region SST / GL Acc / Aging / eInvoice data
      this.SSTEffectiveDate = null;
      this.sstLicenseNo = '';
      this.sstLicenseNoError = '';
      this.selectedSSTSubmitter = null;
      this.sstSubmitterSearch = '';
      this.sstSubmitterError = '';
      this.selectedSSTGLAccNo = null;
      this.sstGLAccSearch = '';
      this.sstGLAccNoError = '';
      this.invoiceTIN = '';
      this.invoiceTINError = '';
      this.tourismTaxNo = '';
      this.tourismTaxNoError = '';
      this.version = '';
      this.selectedPLGLAccNo = null;
      this.plGLAccSearch = '';
      this.plGLAccNoError = '';
      this.selectedAccumulatedPLGLAccNo = null;
      this.accumulatedPLGLAccSearch = '';
      this.accumulatedPLGLAccNoError = '';
      this.selectedDebtorGLAccCode = null;
      this.debtorGLAccSearch = '';
      this.debtorGLAccCodeError = '';
      this.selectedCreditorGLAccCode = null;
      this.creditorGLAccSearch = '';
      this.creditorGLAccCodeError = '';
      this.selectedAgingType = null;
      this.agingTypeSearch = '';
      this.agingTypeError = '';
      this.selectedMonthTitle = null;
      this.monthTitleSearch = '';
      this.monthTitleError = '';
      this.financialMonth = '';
      this.financialMonthError = '';
      this.agingBucket1 = '';
      this.agingBucketsError1 = '';
      this.agingBucket2 = '';
      this.agingBucketsError2 = '';
      this.agingBucket3 = '';
      this.agingBucketsError3 = '';
      this.agingBucket4 = '';
      this.agingBucketsError4 = '';
      this.agingBucket5 = '';
      this.agingBucketsError5 = '';
      this.agingBucket6 = '';
      this.agingBucketsError6 = '';
      this.agingBucket7 = '';
      this.agingBucketsError7 = '';
      this.agingBucket8 = '';
      this.agingBucketsError8 = '';
      this.agingBucket9 = '';
      this.agingBucketsError9 = '';
      this.agingBucket10 = '';
      this.agingBucketsError10 = '';
      this.agingBucket11 = '';
      this.agingBucketsError11 = '';
      this.agingBucket12 = '';
      this.agingBucketsError12 = '';
      this.defaultCreditLimit = '';
      this.defaultCreditLimitError = '';
      this.defaultCreditTerm = '';
      this.defaultCreditTermError = '';
      this.selectedFCurGainGLAcc = null;
      this.fCurGainGLAccSearch = '';
      this.fCurGainGLAccError = '';
      this.selectedFCurLossGLAcc = null;
      this.fCurLossGLAccSearch = '';
      this.fCurLossGLAccError = '';
      //#endregion

      //#region Report Header & Fotter data
      this.reportHeader1 = '';
      this.reportHeaderError1 = '';
      this.reportHeader2 = '';
      this.reportHeaderError2 = '';
      this.reportHeader3 = '';
      this.reportHeaderError3 = '';
      this.reportHeader4 = '';
      this.reportHeaderError4 = '';
      this.reportHeader5 = '';
      this.reportHeaderError5 = '';
      this.reportFooter1 = '';
      this.reportFooterError1 = '';
      this.reportFooter2 = '';
      this.reportFooterError2 = '';

      this.reportHeaderUploadedImage = null;
      this.reportHeaderImageBlobURL = '';
      this.reportHeaderImagePlaceholderMessage = '';
      //#endregion

      //#region Fin Report Header & Footer data
      this.finReportHeader1 = '';
      this.finReportHeaderError1 = '';
      this.finReportHeader2 = '';
      this.finReportHeaderError2 = '';
      this.finReportHeader3 = '';
      this.finReportHeaderError3 = '';
      this.finReportHeader4 = '';
      this.finReportHeaderError4 = '';
      this.finReportHeader5 = '';
      this.finReportHeaderError5 = '';
      this.finReportHeader6 = '';
      this.finReportHeaderError6 = '';

      this.finReportHeaderUploadedImage = null;
      this.finReportHeaderImageBlobURL = '';
      this.finReportHeaderImagePlaceholderMessage = '';

      this.selectedFinReportFooterImage = 1;
      this.finReportFooterUploadedImage1 = null;
      this.finReportFooterUploadedImage2 = null;
      this.finReportFooterUploadedImage3 = null;
      this.finReportFooterImage1BlobURL = '';
      this.finReportFooterImage2BlobURL = '';
      this.finReportFooterImage3BlobURL = '';
      this.finReportFooterImage1PlaceholderMessage = '';
      this.finReportFooterImage2PlaceholderMessage = '';
      this.finReportFooterImage3PlaceholderMessage = '';
      this.finReportFooterDescInput = '';
      this.finReportFooterDesc1 = '';
      this.finReportFooterDesc2 = '';
      this.finReportFooterDesc3 = '';
      //#endregion

      //#region GST Details data
      this.gstLicenseNo = '';
      this.gstLicenseNoError = '';
      this.GSTEffectiveDate = null;
      this.selectedGSTCurrency = null;
      this.gstCurrencySearch = '';
      this.gstCurrencyError = '';
      this.selectedGSTPeriodCategory = null;
      this.gstPeriodCategorySearch = '';
      this.gstPeriodCategoryError = '';
      this.GSTNextFilingDate = null;
      this.selectedGSTARGLAccNo = null;
      this.gstARGLAccNoSearch = '';
      this.gstARGLAccNoError = '';
      this.selectedGSTAPGLAccNo = null;
      this.gstAPGLAccNoSearch = '';
      this.gstAPGLAccNoError = '';
      this.selectedMSICCode = null;
      this.selectedGSTGLAccNoForDeposit = null;
      this.gstGLAccNoForDepositSearch = '';
      this.gstGLAccNoForDepositError = '';
      this.selectedGSTGLAccNoForBadDeptAR = null;
      this.gstGLAccNoForBadDeptARSearch = '';
      this.gstGLAccNoForBadDeptARError = '';
      this.selectedGSTGLAccBadDeptRecover = null;
      this.gstGLAccBadDeptRecoverSearch = '';
      this.gstGLAccBadDeptRecoverError = '';
      this.selectedGSTGLAccBadDeptLiability = null;
      this.gstGLAccBadDeptLiabilitySearch = '';
      this.gstGLAccBadDeptLiabilityError = '';
      this.selectedGSTGLAccNoForBadDeptAP = null;
      this.gstGLAccNoForBadDeptAPSearch = '';
      this.gstGLAccNoForBadDeptAPError = '';
      this.selectedGSTGLAccBadDeptReclaim = null;
      this.gstGLAccBadDeptReclaimSearch = '';
      this.gstGLAccBadDeptReclaimError = '';
      this.selectedGSTGLAccBadDeptClaimable = null;
      this.gstGLAccBadDeptClaimableSearch = '';
      this.gstGLAccBadDeptClaimableError = '';
      this.selectedGSTGLControlAcc = null;
      this.gstGLControlAccSearch = '';
      this.gstGLControlAccError = '';
      //#endregion
    },
    clearEntryMessages: function () {
      this.entryRequired = [];
      this.entrySuccess = [];
      this.entryMessages = [];
      this.entryError = [];
    },
    entryRequiredClicked: function (item) {
      let errorTabs = this.errorMapping.find((e) => e['key'] == item);

      if (this.currentTab != errorTabs['tab'] && errorTabs['tab'] >= 0) {
        this.currentTab = errorTabs['tab'];
      }

      this.$nextTick(() => {
        switch (errorTabs['key']) {
          case 'co_addressline1':
            this.$refs['addressForm'].setFocus('address1');
            break;

          case 'co_addressline2':
            this.$refs['addressForm'].setFocus('address2');
            break;

          case 'co_addressline3':
            this.$refs['addressForm'].setFocus('address3');
            break;

          case 'co_addressline4':
            this.$refs['addressForm'].setFocus('address4');
            break;

          case 'co_postcode':
            this.$refs['addressForm'].setFocus('postcodeDropdown');
            break;

          case 'co_city':
            this.$refs['addressForm'].setFocus('city');
            break;

          default:
            this.$refs[item].setFocus();
            break;
        }
      });
    },

    populateObject: function (data, key, compare) {
      // To populate object into dropdown or input query
      try {
        return data.find((d) => (d[key]?.trim?.() ?? d[key]) == (compare?.trim?.() ?? compare));
      } catch (error) {
        return null;
      }
    },
    populateTextTrim: function (text) {
      // To populate text with trim
      return text ? text.trim() : '';
    },

    checkFields: function () {
      this.clearEntryMessages();

      if (this.financialMonthSearch !== '' && this.selectedFinancialMonth === null) {
        this.financialMonthError = 'Invalid Financial Month';
        this.entryRequired.push({ key: 'financialMonth', value: this.financialMonthError });
      }

      if (!this.companyCode) {
        this.companyCodeError = 'Company Code is required!';
        this.entryRequired.push({ key: 'companyCode', value: 'Company Code is required!' });
      } else {
        if (this.companyCode.length > 2) {
          this.companyCodeError = 'Company Code cannot exceed 2 characters!';
          this.entryRequired.push({ key: 'companyCode', value: 'Company Code cannot exceed 2 characters!' });
        }
      }
      if (!this.co_name) {
        this.nameError = 'Company Name is required!';
        this.entryRequired.push({ key: 'co_name', value: 'Company Name is required!' });
      } else {
        if (this.co_name.length > 60) {
          this.nameError = 'Company Name cannot exceed 60 characters!';
          this.entryRequired.push({ key: 'co_name', value: 'Company Name cannot exceed 60 characters!' });
        }
      }
      if (!this.co_sname) {
        this.snameError = 'Company Short Name is required!';
        this.entryRequired.push({ key: 'co_sname', value: 'Company Short Name is required!' });
      } else {
        if (this.co_sname.length > 20) {
          this.snameError = 'Company Short Name cannot exceed 20 characters!';
          this.entryRequired.push({ key: 'co_sname', value: 'Company Short Name cannot exceed 20 characters!' });
        }
      }
      if (this.regNo.length > 20) {
        this.regNoError = 'Comp Reg. No cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'co_regno', value: 'Comp Reg. No cannot exceed 20 characters!' });
      }
      if (!this.selectedCoType && this.coTypeSearch) {
        this.coTypeError = 'Invalid Company Type';
        this.entryRequired.push({ key: 'coType', value: 'Invalid Company Type' });
      }
      if (!this.addressData.addressLine1.trim()) {
        this.addressErrors.addressLine1 = 'Address Line 1 is required!';
        this.entryRequired.push({ key: 'co_addressline1', value: 'Address Line 1 is required!' });
      } else {
        if (this.addressData.addressLine1.length > 40) {
          this.addressErrors.addressLine1 = 'Address Line 1 cannot exceed 40 characters!';
          this.entryRequired.push({ key: 'co_addressline1', value: 'Address Line 1 cannot exceed 40 characters!' });
        }
      }
      if (this.addressData.addressLine2.length > 40) {
        this.addressErrors.addressLine2 = 'Address Line 2 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_addressline2', value: 'Address Line 2 cannot exceed 40 characters!' });
      }
      if (this.addressData.addressLine3.length > 40) {
        this.addressErrors.addressLine3 = 'Address Line 3 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_addressline3', value: 'Address Line 3 cannot exceed 40 characters!' });
      }
      if (this.addressData.addressLine4.length > 40) {
        this.addressErrors.addressLine4 = 'Address Line 4 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_addressline4', value: 'Address Line 4 cannot exceed 40 characters!' });
      }
      if (this.addressData.city.length > 50) {
        this.addressErrors.city = 'City cannot exceed 50 characters!';
        this.entryRequired.push({ key: 'co_city', value: 'City cannot exceed 50 characters!' });
      }
      if (!this.addressData.postcode) {
        this.addressErrors.postcode = 'Postcode is required!';
        this.entryRequired.push({ key: 'co_postcode', value: 'Postcode is required!' });
      }
      if (this.phone1.length > 20) {
        this.phone1Error = 'Phone no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'co_phone1', value: 'Phone no cannot exceed 20 characters!' });
      }
      if (this.phone2.length > 20) {
        this.phone2Error = 'Phone no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'co_phone2', value: 'Phone no cannot exceed 20 characters!' });
      }
      if (this.co_fax1.length > 20) {
        this.faxError1 = 'Fax no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'co_fax1', value: 'Fax no cannot exceed 20 characters!' });
      }
      if (this.co_fax2.length > 20) {
        this.faxError2 = 'Fax no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'co_fax2', value: 'Fax no cannot exceed 20 characters!' });
      }
      if (this.co_email1.length > 40) {
        this.emailError1 = 'Email cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_email1', value: 'Email cannot exceed 40 characters!' });
      }
      if (this.co_email1.trim() != '') {
        if (!this.co_email1.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g)) {
          this.emailError1 = 'Email input is not in correct format!';
          this.entryRequired.push({ key: 'co_email1', value: 'Email input is not in correct format!' });
        }
      }
      if (this.co_email2.length > 40) {
        this.emailError2 = 'Email cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_email2', value: 'Email cannot exceed 40 characters!' });
      }
      if (this.co_email2.trim() != '') {
        if (!this.co_email2.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g)) {
          this.emailError2 = 'Email input is not in correct format!';
          this.entryRequired.push({ key: 'co_email2', value: 'Email input is not in correct format!' });
        }
      }
      if (this.co_businessDesc.length > 200) {
        this.businessDescError = 'Business Description cannot exceed 200 characters!';
        this.entryRequired.push({
          key: 'co_businessDesc',
          value: 'Business Description cannot exceed 200 characters!',
        });
      }

      if (!this.selectedAgingType && this.agingTypeSearch) {
        this.agingTypeError = 'Invalid Aging Type!';
        this.entryRequired.push({ key: 'agingType', value: 'Invalid Aging Type!' });
      }
      if (!this.selectedMonthTitle && this.monthTitleSearch) {
        this.monthTitleError = 'Invalid Month Title!';
        this.entryRequired.push({ key: 'monthTitle', value: 'Invalid Month Title!' });
      }
      if (this.co_website.length > 40) {
        this.websiteError = 'Website (URL) cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'co_website', value: 'Website (URL) cannot exceed 40 characters!' });
      }
      if (this.epfNo.length > 15) {
        this.epfNoError = 'Company EPF No cannot exceed 15 characters!';
        this.entryRequired.push({ key: 'epfNo', value: 'Company EPF No cannot exceed 15 characters!' });
      }
      if (!this.selectedEPFCategory && this.epfCategorySearch) {
        this.epfCategoryError = 'Invalid EPF Category!';
        this.entryRequired.push({ key: 'epfCategory', value: 'Invalid EPF Category!' });
      }
      if (this.socsoNo.length > 15) {
        this.socsoNoError = 'Company SOCSO No cannot exceed 15 characters!';
        this.entryRequired.push({ key: 'socsoNo', value: 'Company SOCSO No cannot exceed 15 characters!' });
      }
      if (!this.selectedSOCSOCategory && this.socsoCategorySearch) {
        this.socsoCategoryError = 'Invalid SOCSO Category!';
        this.entryRequired.push({ key: 'socsoCategory', value: 'Invalid SOCSO Category!' });
      }
      if (this.incomeTaxNo.length > 15) {
        this.incomeTaxNoError = 'Company Income Tax No cannot exceed 15 characters!';
        this.entryRequired.push({ key: 'incomeTaxNo', value: 'Company Income Tax No cannot exceed 15 characters!' });
      }

      if (this.sstLicenseNo.length > 50) {
        this.sstLicenseNoError = 'Company SST License No cannot exceed 50 characters!';
        this.entryRequired.push({ key: 'sstLicenseNo', value: 'Company SST License No cannot exceed 50 characters!' });
      }
      if (!this.selectedSSTSubmitter && this.sstSubmitterSearch) {
        this.sstSubmitterError = 'Invalid SST Submitter!';
        this.entryRequired.push({ key: 'sstSubmitter', value: 'Invalid SST Submitter!' });
      }
      if (!this.selectedSSTGLAccNo && this.sstGLAccSearch) {
        this.sstGLAccNoError = 'Invalid SST GL Acc No!';
        this.entryRequired.push({ key: 'sstGLAccNo', value: 'Invalid SST GL Acc No!' });
      }
      if (this.invoiceTIN.length > 13) {
        this.invoiceTINError = 'Company Invoice TIN No cannot exceed 13 characters!';
        this.entryRequired.push({ key: 'invoiceTIN', value: 'Company Invoice TIN cannot exceed 13 characters!' });
      }
      if (this.tourismTaxNo.length > 17) {
        this.tourismTaxNoError = 'Company Tourism Tax No cannot exceed 17 characters!';
        this.entryRequired.push({ key: 'tourismTaxNo', value: 'Company Tourism Tax No cannot exceed 17 characters!' });
      }
      if (!this.selectedPLGLAccNo && this.plGLAccSearch) {
        this.plGLAccNoError = 'Invalid PL GL Acc No!';
        this.entryRequired.push({ key: 'plGLAccNo', value: 'Invalid PL GL Acc No!' });
      }
      if (!this.selectedAccumulatedPLGLAccNo && this.accumulatedPLGLAccSearch) {
        this.accumulatedPLGLAccNoError = 'Invalid Accumulated PL GL Acc No!';
        this.entryRequired.push({ key: 'accumulatedPLGLAccNo', value: 'Invalid Accumulated PL GL Acc No!' });
      }
      if (!this.selectedDebtorGLAccCode && this.debtorGLAccSearch) {
        this.debtorGLAccCodeError = 'Invalid Debtor PL GL Acc Code!';
        this.entryRequired.push({ key: 'debtorGLAccCode', value: 'Invalid Debtor PL GL Acc Code!' });
      }
      if (!this.selectedCreditorGLAccCode && this.creditorGLAccSearch) {
        this.creditorGLAccCodeError = 'Invalid Creditor PL GL Acc Code!';
        this.entryRequired.push({ key: 'creditorGLAccCode', value: 'Invalid Creditor PL GL Acc Code!' });
      }
      if (!this.selectedFCurGainGLAcc && this.fCurGainGLAccSearch) {
        this.fCurGainGLAccError = 'Invalid F. Cur Gain GL Acc!';
        this.entryRequired.push({ key: 'fCurGainGLAcc', value: 'Invalid F. Cur Gain GL Acc!' });
      }
      if (!this.selectedFCurLossGLAcc && this.fCurLossGLAccSearch) {
        this.fCurLossGLAccError = 'Invalid F. Cur Loss GL Acc!';
        this.entryRequired.push({ key: 'fCurLossGLAcc', value: 'Invalid F. Cur Loss GL Acc!' });
      }

      if (this.agingBucket1 != 0 && this.agingTypeSearch === 'Month') {
        this.agingBucketsError1 = 'Aging Bucket 1 must be 0!';
        this.entryRequired.push({ key: 'agingBucket1', value: 'Aging Bucket 1 must be 0!' });
      }
      if (this.agingBucket1.length > 9) {
        this.agingBucketsError1 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket1', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket2.length > 9) {
        this.agingBucketsError2 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket2', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket3.length > 9) {
        this.agingBucketsError3 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket3', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket4.length > 9) {
        this.agingBucketsError4 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket4', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket5.length > 9) {
        this.agingBucketsError5 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket5', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket6.length > 9) {
        this.agingBucketsError6 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket6', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket7.length > 9) {
        this.agingBucketsError7 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket7', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket8.length > 9) {
        this.agingBucketsError8 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket8', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket9.length > 9) {
        this.agingBucketsError9 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket9', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket10.length > 9) {
        this.agingBucketsError10 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket10', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket11.length > 9) {
        this.agingBucketsError11 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket11', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.agingBucket12.length > 9) {
        this.agingBucketsError12 = 'Aging Bucket cannot exceed 9 digits';
        this.entryRequired.push({ key: 'agingBucket12', value: 'Aging Bucket cannot exceed 9 digits' });
      }
      if (this.defaultCreditLimit.length > 7) {
        this.defaultCreditLimitError = 'Default Credit Limit cannot exceed 7 digits!';
        this.entryRequired.push({ key: 'defaultCreditLimit', value: 'Default Credit Limit cannot exceed 7 digits!' });
      }
      if (this.defaultCreditTerm.length > 4) {
        this.defaultCreditTermError = 'Default Credit Term cannot exceed 4 digits!';
        this.entryRequired.push({ key: 'defaultCreditTerm', value: 'Default Credit Term cannot exceed 4 digits!' });
      }

      if (this.reportHeader1.length > 150) {
        this.reportHeaderError1 = 'Report Header 1 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportHeader1', value: 'Report header 1 cannot exceed 150 characters!' });
      }
      if (this.reportHeader2.length > 150) {
        this.reportHeaderError2 = 'Report Header 2 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportHeader2', value: 'Report header 2 cannot exceed 150 characters!' });
      }
      if (this.reportHeader3.length > 150) {
        this.reportHeaderError3 = 'Report Header 3 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportHeader3', value: 'Report header 3 cannot exceed 150 characters!' });
      }
      if (this.reportHeader4.length > 150) {
        this.reportHeaderError4 = 'Report Header 4 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportHeader4', value: 'Report header 4 cannot exceed 150 characters!' });
      }
      if (this.reportHeader5.length > 150) {
        this.reportHeaderError5 = 'Report Header 5 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportHeader5', value: 'Report header 5 cannot exceed 150 characters!' });
      }
      if (this.reportFooter1.length > 150) {
        this.reportFooterError1 = 'Report Footer 1 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportFooter1', value: 'Report footer 1 cannot exceed 150 characters!' });
      }
      if (this.reportFooter2.length > 150) {
        this.reportFooterError2 = 'Report Footer 2 cannot exceed 150 characters!';
        this.entryRequired.push({ key: 'reportFooter2', value: 'Report Footer 2 cannot exceed 150 characters!' });
      }

      if (this.finReportHeader1.length > 150) {
        this.finReportHeaderError1 = 'Fin Report Header 1 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader1',
          value: 'Fin Report Header 1 cannot exceed 150 characters!',
        });
      }
      if (this.finReportHeader2.length > 150) {
        this.finReportHeaderError2 = 'Fin Report Header 2 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader2',
          value: 'Fin Report Header 2 cannot exceed 150 characters!',
        });
      }
      if (this.finReportHeader3.length > 150) {
        this.finReportHeaderError3 = 'Fin Report Header 3 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader3',
          value: 'Fin Report Header 3 cannot exceed 150 characters!',
        });
      }
      if (this.finReportHeader4.length > 150) {
        this.finReportHeaderError4 = 'Fin Report Header 4 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader4',
          value: 'Fin Report Header 4 cannot exceed 150 characters!',
        });
      }
      if (this.finReportHeader5.length > 150) {
        this.finReportHeaderError5 = 'Fin Report Header 5 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader5',
          value: 'Fin Report Header 5 cannot exceed 150 characters!',
        });
      }
      if (this.finReportHeader6.length > 150) {
        this.finReportHeaderError6 = 'Fin Report Header 6 cannot exceed 150 characters!';
        this.entryRequired.push({
          key: 'finReportHeader6',
          value: 'Fin Report Header 6 cannot exceed 150 characters!',
        });
      }

      if (this.gstLicenseNo.length > 50) {
        this.gstLicenseNoError = 'Company GST License No cannot exceed 50 characters!';
        this.entryRequired.push({ key: 'gstLicenseNo', value: 'Company GST License No cannot exceed 50 characters!' });
      }
      if (!this.selectedGSTCurrency && this.gstCurrencySearch) {
        this.gstCurrencyError = 'Invalid GST Currency!';
        this.entryRequired.push({ key: 'gstCurrency', value: 'Invalid GST Currency!' });
      }
      if (!this.selectedGSTPeriodCategory && this.gstPeriodCategorySearch) {
        this.gstPeriodCategoryError = 'Invalid GST Period Category!';
        this.entryRequired.push({ key: 'gstPeriodCategory', value: 'Invalid GST Period Category!' });
      }
      if (!this.selectedGSTARGLAccNo && this.gstARGLAccNoSearch) {
        this.gstARGLAccNoError = 'Invalid GST AR GL Acc No!';
        this.entryRequired.push({ key: 'gstARGLAccNo', value: 'Invalid GST AR GL Acc No!' });
      }
      if (!this.selectedGSTAPGLAccNo && this.gstAPGLAccNoSearch) {
        this.gstAPGLAccNoError = 'Invalid GST AP GL Acc No!';
        this.entryRequired.push({ key: 'gstAPGLAccNo', value: 'Invalid GST AP GL Acc No!' });
      }
      if (!this.selectedGSTGLAccNoForDeposit && this.gstGLAccNoForDepositSearch) {
        this.gstGLAccNoForDepositError = 'Invalid GST GL Acc No For Deposit!';
        this.entryRequired.push({ key: 'gstGLAccNoForDeposit', value: 'Invalid GST GL Acc No For Deposit!' });
      }
      if (!this.selectedGSTGLAccNoForBadDeptAR && this.gstGLAccNoForBadDeptARSearch) {
        this.gstGLAccNoForBadDeptARError = 'Invalid GST GL Acc No For Bad Dept AR!';
        this.entryRequired.push({ key: 'gstGLAccNoForBadDeptAR', value: 'Invalid GST GL Acc No For Bad Dept AR!' });
      }
      if (!this.selectedGSTGLAccBadDeptRecover && this.gstGLAccBadDeptRecoverSearch) {
        this.gstGLAccBadDeptRecoverError = 'Invalid GST GL Acc Bad Dept Recover!';
        this.entryRequired.push({ key: 'gstGLAccBadDeptRecover', value: 'Invalid GST GL Acc Bad Dept Recover!' });
      }
      if (!this.selectedGSTGLAccBadDeptLiability && this.gstGLAccBadDeptLiabilitySearch) {
        this.gstGLAccBadDeptLiabilityError = 'Invalid GST GL Acc Bad Dept Liability!';
        this.entryRequired.push({ key: 'gstGLAccBadDeptLiability', value: 'Invalid GST GL Acc Bad Dept Liability!' });
      }
      if (!this.selectedGSTGLAccNoForBadDeptAP && this.gstGLAccNoForBadDeptAPSearch) {
        this.gstGLAccNoForBadDeptAPError = 'Invalid GST GL Acc No For Bad Dept AP!';
        this.entryRequired.push({ key: 'gstGLAccNoForBadDeptAP', value: 'Invalid GST GL Acc No For Bad Dept AP!' });
      }
      if (!this.selectedGSTGLAccBadDeptReclaim && this.gstGLAccBadDeptReclaimSearch) {
        this.gstGLAccBadDeptReclaimError = 'Invalid GST GL Acc Bad Dept Reclaim!';
        this.entryRequired.push({ key: 'gstGLAccBadDeptReclaim', value: 'Invalid GST GL Acc Bad Dept Reclaim!' });
      }
      if (!this.selectedGSTGLAccBadDeptClaimable && this.gstGLAccBadDeptClaimableSearch) {
        this.gstGLAccBadDeptClaimableError = 'Invalid GST GL Acc Bad Dept Claimable!';
        this.entryRequired.push({ key: 'gstGLAccBadDeptClaimable', value: 'Invalid GST GL Acc Bad Dept Claimable!' });
      }
      if (!this.selectedGSTGLControlAcc && this.gstGLControlAccSearch) {
        this.gstGLControlAccError = 'Invalid GST GL ControlAcc!';
        this.entryRequired.push({ key: 'gstGLControlAcc', value: 'Invalid GST GL ControlAcc!' });
      }

      if (this.gstEnabled === true) {
        if (!this.gstLicenseNo) {
          this.gstLicenseNoError = 'Company GST License No is required!';
          this.entryRequired.push({ key: 'gstLicenseNo', value: 'Company GST License No is required!' });
        }
        if (this.gstLicenseNo.length > 13) {
          this.gstLicenseNoError = 'Company GST License No cannot be greater than 13';
          this.entryRequired.push({ key: 'gstLicenseNo', value: 'Company GST License No cannot be greater than 13' });
        }
        if (!this.selectedGSTCurrency) {
          this.gstCurrencyError = 'Company GST Currency is required!';
          this.entryRequired.push({ key: 'gstCurrency', value: 'Company GST Currency is required!' });
        }
        if (!this.selectedGSTPeriodCategory) {
          this.gstPeriodCategoryError = 'Company GST Period Category is required!';
          this.entryRequired.push({ key: 'gstPeriodCategory', value: 'Company GST Period Category is required!' });
        }
        if (!this.GSTEffectiveDate) {
          this.GSTEffectiveDateError = 'GST Effective Date is required!';
          this.entryRequired.push({ key: 'gstEffectiveDate', value: 'GST Effective Date is required!' });
        }
      }

      return this.entryRequired.length > 0;
    },

    saveClicked: async function () {
      const isNotValid = this.checkFields();
      if (isNotValid) {
        return;
      }

      let dataToPost = {
        action: this.popupEntryTitle[0],
        LastUpdate: this.lastUpdate,

        //#region from General Details tab
        companycode: this.companyCode,
        companyname: this.co_name,
        companyshortname: this.co_sname,
        companyregisternumber: this.regNo,
        companydate: this.establishDate?.formatString?.('yyyy-MM-dd HH:mm') ?? null,
        parent: this.selectedCoType?.['Company Type'] ?? '',
        companyaddress1: this.addressData['addressLine1'],
        companyaddress2: this.addressData['addressLine2'],
        companyaddress3: this.addressData['addressLine3'],
        companyaddress4: this.addressData['addressLine4'],
        companypostcode: this.addressData['postcode'],
        companycity: this.addressData['city'],
        companystate: this.addressData['state'],
        companycountry: this.addressData['country'],
        companytelno: this.phone1,
        companytelno2: this.phone2,
        companyfaxno: this.co_fax1,
        companyfaxno2: this.co_fax2,
        companyemail: this.co_email1,
        companyemail2: this.co_email2,
        co_businessdesc: this.co_businessDesc,
        //#endregion

        //#region from Other Details tab
        courl: this.co_website,
        companyepfno: this.epfNo,
        epfcategory: this.selectedEPFCategory?.['EPFCategory'] ?? '0',
        companysocso: this.socsoNo,
        socsocategory: this.selectedSOCSOCategory?.['SocsoCategory'] ?? '0',
        companyincometax: this.incomeTaxNo,
        //#endregion

        //region from SST / GL Acc / Aging / eInvoice
        cogstenabled: this.sstEnabled ? '1' : '0',
        cogsteffectivedate: this.SSTEffectiveDate?.formatString?.('yyyy-MM-dd HH:mm') ?? null,
        cogstlicense: this.sstLicenseNo,
        gst: this.selectedSSTSubmitter?.['EmployeeID'] ?? '',
        cogstglacc: this.selectedSSTGLAccNo?.['Acc No'] ?? '',
        sstvalidate: this.sstValidationValue?.value ?? '0',
        servicetypecode: this.sstServiceTypeCode ?? '',
        co_tin: this.invoiceTIN,
        co_tourismtaxno: this.tourismTaxNo,
        // co_einvoiceversion: this.version, // version is controlled from the backend, shouldn't allow users to change
        pnlacc: this.selectedPLGLAccNo?.['Acc No'] ?? '',
        accpnlacc: this.selectedAccumulatedPLGLAccNo?.['Acc No'] ?? '',
        debtorctrlacc: this.selectedDebtorGLAccCode?.['Acc No'] ?? '',
        creditorctrlacc: this.selectedCreditorGLAccCode?.['Acc No'] ?? '',
        ageingtype: this.selectedAgingType?.['Aging Type'] ?? '',
        soa_mthname: this.selectedMonthTitle?.['Code'] ?? '',
        //financialmth: this.financialMonth,
        //financialmth: this.selectedFinancialMonth["integer"],
        financialmth: this.selectedFinancialMonth !== null ? this.selectedFinancialMonth['integer'] : '0',
        companyduration1: this.agingBucket1,
        companyduration2: this.agingBucket2,
        companyduration3: this.agingBucket3,
        companyduration4: this.agingBucket4,
        coduration5: this.agingBucket5,
        coduration6: this.agingBucket6,
        coduration7: this.agingBucket7,
        coduration8: this.agingBucket8,
        coduration9: this.agingBucket9,
        coduration10: this.agingBucket10,
        coduration11: this.agingBucket11,
        coduration12: this.agingBucket12,
        defaultcrlimit: this.defaultCreditLimit,
        defaultcreditterm: this.defaultCreditTerm,
        foreigncurrencygainglacc: this.selectedFCurGainGLAcc?.['Acc No'] ?? '',
        foreigncurrencylossglacc: this.selectedFCurLossGLAcc?.['Acc No'] ?? '',
        //endregion

        //#region from Report Header tab
        reporthdrline1: this.reportHeader1,
        reporthdrline2: this.reportHeader2,
        reporthdrline3: this.reportHeader3,
        reporthdrline4: this.reportHeader4,
        reporthdrline5: this.reportHeader5,
        reportftrline1: this.reportFooter1,
        reportftrline2: this.reportFooter2,
        //#endregion

        //#region from Finn Report Header tab
        finreportheader1: this.finReportHeader1,
        finreportheader2: this.finReportHeader2,
        finreportheader3: this.finReportHeader3,
        finreportheader4: this.finReportHeader4,
        finreportheader5: this.finReportHeader5,
        finreportheader6: this.finReportHeader6,
        //endregion

        //#region from Fin Report Footer tab (for footer desc)
        finreportfooterdesc1: this.finReportFooterDesc1,
        finreportfooterdesc2: this.finReportFooterDesc2,
        finreportfooterdesc3: this.finReportFooterDesc3,
        //#endregion

        //#region from GST Details tab
        cogst2enabled: this.gstEnabled ? '1' : '0',
        cogst2license: this.gstLicenseNo,
        cogst2effectivedate: this.GSTEffectiveDate?.formatString?.('yyyy-MM-dd HH:mm') ?? null,
        cogst2reportingcurrency: this.selectedGSTCurrency?.['Currency Code'] ?? '',
        cogst2taxableperiodcategory: this.selectedGSTPeriodCategory?.['Period Category'] ?? '',
        cogst2nextfilingdate: this.GSTNextFilingDate?.formatString?.('yyyy-MM-dd HH:mm') ?? null,
        cogst2arglacc: this.selectedGSTARGLAccNo?.['Acc No'] ?? '',
        cogst2apglacc: this.selectedGSTAPGLAccNo?.['Acc No'] ?? '',
        cogst2majorindustrycode: this.selectedMSICCode?.['MSIC Code'] ?? '',
        cogst2suspendondepositarglacc: this.selectedGSTGLAccNoForDeposit?.['Acc No'] ?? '',
        cogst2baddebtarglacc: this.selectedGSTGLAccNoForBadDeptAR?.['Acc No'] ?? '',
        cogst2baddebtrecoveredarglacc: this.selectedGSTGLAccBadDeptRecover?.['Acc No'] ?? '',
        cogst2baddebtarliability: this.selectedGSTGLAccBadDeptLiability?.['Acc No'] ?? '',
        cogst2baddebtapglacc: this.selectedGSTGLAccNoForBadDeptAP?.['Acc No'] ?? '',
        cogst2baddebtpaidapglacc: this.selectedGSTGLAccBadDeptReclaim?.['Acc No'] ?? '',
        cogst2baddebtapclaimable: this.selectedGSTGLAccBadDeptClaimable?.['Acc No'] ?? '',
        cogst2controlglacc: this.selectedGSTGLControlAcc?.['Acc No'] ?? '',
        //#endregion

        attachment: [],
      };
      if (this.companyLogoUploadedImage) {
        dataToPost.attachment.push({
          type: 'logo',
          attfile: await this.companyLogoUploadedImage.toBase64(true), // remove MIME type before sending to API
        });
      }
      if (this.reportHeaderUploadedImage) {
        dataToPost.attachment.push({
          type: 'header',
          attfile: await this.reportHeaderUploadedImage.toBase64(true),
        });
      }
      if (this.finReportHeaderUploadedImage) {
        dataToPost.attachment.push({
          type: 'headerfin',
          attfile: await this.finReportHeaderUploadedImage.toBase64(true),
        });
      }
      if (this.finReportFooterUploadedImage1) {
        dataToPost.attachment.push({
          type: 'footerfin1',
          attfile: await this.finReportFooterUploadedImage1.toBase64(true),
        });
      }
      if (this.finReportFooterUploadedImage2) {
        dataToPost.attachment.push({
          type: 'footerfin2',
          attfile: await this.finReportFooterUploadedImage2.toBase64(true),
        });
      }
      if (this.finReportFooterUploadedImage3) {
        dataToPost.attachment.push({
          type: 'footerfin3',
          attfile: await this.finReportFooterUploadedImage3.toBase64(true),
        });
      }

      this.$fullscreenLoading('Saving Company');
      let result = await this.$apiCallV2('/Company/SaveCompany', dataToPost);
      this.$fullscreenLoading();

      if (result[0]['Results'] == 0) {
        // Save successful
        this.entrySuccess.push(`${result[0]['ErrMsg']}`);
        this.selectedCompany = [];
        this.companyList = [];
        this.$nextTick(async () => await this.getCompany());
        this.viewMode = true;
        this.popupEntryTitle = 'View Company';
      } else {
        // Save error
        this.entryError.push(`There was an error editting company: ${result[0]['ErrMsg']}`);
      }
    },
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Company') {
        x.data.forEach((k) => {
          //key in menu same as control value from api
          let selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });
    await this.getCompany();
    await this.getCoType();
    await this.getEPFCategoryDefault();
    await this.getSOCSOCategoryDefault();
    await this.getAgingType();
    await this.getMonthTitle();

    this.$fullscreenLoading('Getting MSIC Codes');
    this.MSICData = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'MSIC' });

    this.$fullscreenLoading('Getting GL Accounts');
    const GLacc = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'GLACCOUNT' });

    //#region API call SST / GL Acc / Aging / eInvoice tab
    this.$fullscreenLoading('Getting SST Submitter');
    this.SSTSubmitterData = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'EMPLOYEE' });

    this.SSTGLAccNoData = GLacc;
    this.PLGLAccNoData = GLacc;
    this.AccumulatedPLGLAccNoData = GLacc;
    this.DebtorGLAccCodeData = GLacc;
    this.CreditorGLAccCodeData = GLacc;
    this.FCurGainGLAccData = GLacc;
    this.FCurLossGLAccData = GLacc;
    //#endregion

    //#region API call GST Details tab
    this.$fullscreenLoading('Getting GST Currency');
    this.GSTCurrencyData = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'CURRENCY' });

    this.$fullscreenLoading('Getting GST Period Category');
    this.GSTPeriodCategoryData = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'GSTPERIODCATEGORY' });

    this.GSTARGLAccNoData = GLacc;
    this.GSTAPGLAccNoData = GLacc;
    this.GSTGLAccNoForDepositData = GLacc;
    this.GSTGLAccNoForBadDeptARData = GLacc;
    this.GSTGLAccBadDeptRecoverData = GLacc;
    this.GSTGLAccBadDeptLiabilityData = GLacc;
    this.GSTGLAccNoForBadDeptAPData = GLacc;
    this.GSTGLAccBadDeptReclaimData = GLacc;
    this.GSTGLAccBadDeptClaimableData = GLacc;
    this.GSTGLControlAccData = GLacc;
    //#endregion

    this.$fullscreenLoading('Getting Post Codes');
    this.postcodeList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'postcode' });

    this.$fullscreenLoading('Getting States');
    this.stateList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'state' });

    this.$fullscreenLoading('Getting Countries');
    this.countryList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' });

    this.$fullscreenLoading('Getting Image URLs');
    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');
    this.coFooterFin2 = (await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin2'))[0].SettingsValue;
    this.coFooterFin3 = (await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin3'))[0].SettingsValue;

    this.$fullscreenLoading();

    if (this.$route.query['new']) {
      this.menuClicked({ title: 'New Company' });
    }
  },
  watch: {
    popupEntryShow: async function (val) {
      if (!val && this.$route.query['new'] && !this.viewMode) {
        // When the cancel is click but is not saved (for query new=1)
        let closeWindow = await this.$showAlert(
          'warning',
          'Cancelling Company Creation',
          `You have not created an Company profile.\nAre you sure you want to cancel?\n\nIf you cancel, this page will close.`,
          [{ text: 'Continue Creating' }, { text: 'Cancel Creating', color: 'red', textColor: 'white' }],
        );

        if (closeWindow == 'Cancel Creating') {
          // If user choose to cancel the creating process, it will close the tab
          window.close();
        } else {
          this.popupEntryShow = true;
        }
      } else if (!val && this.$route.query['new'] && this.viewMode) {
        window.close();
      }
    },
    agingTypeSearch: function (newValue, oldValue) {
      if (newValue === '') {
        this.clearAllAgingBuckets();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.company-main {
  padding: 10px 45px;
  font-size: 12px;
  display: flex;

  > .right-container {
    background-color: white;
    padding: 5px;
    border-radius: 5px;
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
  }
}

.checkbox-container {
  position: relative;
  margin-top: 0px;
}

.gen-details-right {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.flex-row {
  display: flex;
  align-items: center;

  > * {
    width: 100%;
  }

  > :not(:first-child) {
    margin-left: 5px;
  }

  > .rowTitle {
    white-space: nowrap;
    margin: 0 20px 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.company-logo-img {
  height: 180px;
  width: 700px;
  object-fit: contain; // resize the image while maintaining aspect ratio so that the logo doesn't look distorted
  object-position: left top;
}

.fin-report-footer-image-box-container {
  > .image-selector-and-uploader {
    display: flex;
    flex-direction: row;

    > .image-selector-buttons {
      display: flex;
      flex-direction: column;
      justify-content: center;

      > button {
        margin: 3px 0 3px 0;
      }

      > .image-unselected {
        background-color: lightgray;
      }
    }

    .image-uploader {
      margin-left: 40px;
      flex: 1;

      img {
        height: 25mm;
        width: 130mm;
      }
    }
  }
}

.image-placeholder {
  height: 25mm;
  width: 130mm;
  display: flex;
  justify-content: center;
  align-items: center;

  // taken and modified from https://stackoverflow.com/a/28974253
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M 0 0 L 100 100' stroke='darkgray' /><path d='M 0 100 L 100 0' stroke='darkgray' /></svg>");
  background-color: lightgray;

  > .placeholder-text-container {
    background-color: lightgray;
    font-size: 16px;
  }
}
</style>
